/**
 * Peter & Pauls 2026 — global custom JS.
 *
 * Native only. No jQuery on this store (decided 2026-08-24, see CLAUDE.md):
 * Horizon's own components are custom elements with their own event model, and
 * running a second framework over the same DOM is where that hurts most.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  /* =========================================================================
   * 0. 🔴 THE SCROLL CONTAINER IS NOT `window` ON DESKTOP
   *
   * Horizon uses a "squeeze layout" above 990px. assets/base.css sets:
   *
   *     @media (min-width: 990px){
   *       html:has(.page-wrapper), html:has(.page-wrapper) body{
   *         height: 100dvh; overflow: hidden;
   *       }
   *       .page-wrapper{ height: 100dvh; overflow-y: auto; }
   *     }
   *
   * So on desktop `.page-wrapper` scrolls, `window.scrollY` is permanently 0,
   * and `window`'s scroll event NEVER fires. Below 990px the document root
   * scrolls natively (so the mobile address bar can hide) and window works.
   *
   * This is what silently killed the header shrink on desktop for two days:
   * `.header_scrolled` was never applied, and the shrink only appeared to work
   * because a second CSS trigger (Horizon's `data-sticky-state`) was doing all
   * the work. Verified in-browser 2026-08-25: `window.scrollY === 0` at every
   * scroll position, `.page-wrapper` is the only scrollable element on the page.
   *
   * Mirrors getScrollContainer() / getScrollEventTarget() in
   * assets/scroll-container.js — that file is an ES module, so it cannot be
   * imported here. Keep the two in sync.
   * ====================================================================== */
  var SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');

  function getScroller() {
    if (SQUEEZE_QUERY.matches) {
      return document.querySelector('.page-wrapper') || document.scrollingElement || root;
    }
    return document.scrollingElement || root;
  }

  function getScrollEventTarget() {
    if (SQUEEZE_QUERY.matches) {
      return document.querySelector('.page-wrapper') || document;
    }
    return document;
  }

  function getScrollTop() {
    var el = getScroller();
    return el ? el.scrollTop : 0;
  }

  /* =========================================================================
   * 1. Scrolled state
   * Adds `.header_scrolled` to <html>. _header.scss and _headerNav.scss use it
   * to shrink the logo, row 2's padding and row 3's padding together, on one
   * shared .25s transition.
   *
   * 🔴 TWO THRESHOLDS, NOT ONE — this is hysteresis, not a bug (2026-08-25).
   * A single threshold means any scroll that hovers on it (trackpad jitter,
   * momentum settling, a short flick) re-crosses it repeatedly, and each cross
   * restarts a .25s size animation, so the header visibly bounces between its
   * two sizes. Shrinking at 60 and only releasing back at 20 leaves a 40px dead
   * band, which no amount of jitter can cross in one gesture.
   *
   * Do NOT collapse these to one number, and do NOT add a second trigger for
   * the same styles in CSS. The build before this one also keyed the shrink off
   * Horizon's `data-sticky-state='active'`, which flips at its own, different
   * scroll position (the moment the header sticks) — two triggers at two
   * positions is what made the shrink land in two steps and flip-flop between
   * them.
   * ====================================================================== */
  var SHRINK_AT = 60;
  var RELEASE_AT = 20;

  var scrolled = false;
  var scrollTicking = false;

  function applyScrolled() {
    scrollTicking = false;

    var y = getScrollTop();
    var next = scrolled ? y > RELEASE_AT : y > SHRINK_AT;
    if (next === scrolled) return;

    scrolled = next;

    // Class and custom property are written in the SAME synchronous block, so
    // the browser coalesces them into one style recalculation and both
    // transitions start on the same frame. See section 2.
    root.classList.toggle('header_scrolled', scrolled);
    writeNavOffset();
  }

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(applyScrolled);
  }

  // The scroll target swaps at the 990px boundary, so the listener has to move
  // with it — binding once at load leaves the header dead after a resize across
  // the breakpoint.
  var boundTarget = null;

  function bindScroll() {
    var target = getScrollEventTarget();
    if (target === boundTarget) return;

    if (boundTarget) boundTarget.removeEventListener('scroll', onScroll);
    boundTarget = target;
    boundTarget.addEventListener('scroll', onScroll, { passive: true });
  }

  /* =========================================================================
   * 2. Pin row 3 flush under row 2
   *
   * Horizon sticks only `.header-section` (row 2); row 3 is a separate section
   * and pins itself at `top: var(--pp-header-h)`.
   *
   * 🔴 Read this before changing anything here (2026-08-25).
   *
   * Row 2's height ANIMATES over .25s when it shrinks. The obvious approach —
   * measure the header every frame and write its live height into
   * --pp-header-h — does NOT work reliably, and a black gap opens between rows
   * 2 and 3 for the length of the transition. Measured in-browser: up to ~9px.
   * Both a ResizeObserver (fires after the frame's layout is committed) and a
   * requestAnimationFrame loop (fine at 60fps, starved when the browser
   * throttles rAF) leave row 3 chasing a value it can never catch.
   *
   * The fix is to stop chasing. --pp-header-h is set ONCE per state change, to
   * the FINAL height, and `.header_nav_section` transitions `top` with exactly
   * the same duration and easing as row 2's padding (.25s ease). Two properties
   * interpolating on identical curves between identical endpoints are in
   * lockstep on every frame, at any frame rate, with no JS running at all
   * during the animation. The gap is not "small", it is impossible.
   *
   * Requirements that keep it true — all three matter:
   *   1. `.header_nav_section`'s `top` transition MUST stay .25s ease, the same
   *      as `.header__row--top`'s padding transition in _header.scss.
   *   2. Nothing may write --pp-header-h mid-transition. A ResizeObserver
   *      firing during the shrink would restart the `top` transition from a
   *      part-way value every frame and reintroduce the lag — hence the
   *      `settling` guard below.
   *   3. Both endpoint heights are measured up front, with transitions
   *      suppressed, inside ONE synchronous block so no intermediate state is
   *      ever painted.
   * ====================================================================== */
  var heights = { rest: null, shrunk: null, navRest: null, navShrunk: null };

  function measureHeights() {
    var header = document.querySelector('#header-component');
    if (!header) return;

    // Row 3's wrapper. Absent on templates without the band, and 0 tall below
    // 990 where `.header_nav_sec` is `display: none` — both of which make
    // --pp-chrome-h collapse to --pp-header-h on their own, with no branch here.
    var nav = document.querySelector('.header_nav_section');

    var wasScrolled = root.classList.contains('header_scrolled');

    // `.pp_measuring` kills every transition inside the header (see
    // _header.scss), so toggling the state below cannot start an animation.
    // Everything from here to the class removal is synchronous: the browser
    // cannot paint mid-task, so none of this is visible.
    root.classList.add('pp_measuring');

    root.classList.add('header_scrolled');
    heights.shrunk = header.getBoundingClientRect().height;
    heights.navShrunk = nav ? nav.getBoundingClientRect().height : 0;

    root.classList.remove('header_scrolled');
    heights.rest = header.getBoundingClientRect().height;
    heights.navRest = nav ? nav.getBoundingClientRect().height : 0;

    root.classList.toggle('header_scrolled', wasScrolled);
    header.getBoundingClientRect(); // flush, so the restored state is the baseline

    root.classList.remove('pp_measuring');
  }

  function writeNavOffset() {
    var h = scrolled ? heights.shrunk : heights.rest;

    if (h == null) {
      var header = document.querySelector('#header-component');
      if (!header) return;
      h = header.getBoundingClientRect().height;
    }

    // `.header-section` sticks at top:-1px, so the header's visual bottom edge
    // when stuck is (height - 1). Floored, so a fractional device pixel can
    // never leave a hairline: row 3 tucks up to 1px UNDER row 2, invisible
    // because row 2 is opaque and paints above it.
    var headerBottom = Math.floor(h) - 1;
    root.style.setProperty('--pp-header-h', headerBottom + 'px');

    // --pp-chrome-h — the bottom edge of ALL the sticky chrome, i.e. row 3's
    // bottom rather than row 2's. Anything else on the site that pins itself
    // under the header (the FAQ page's category tabs) uses this, not
    // --pp-header-h, or it would sit behind the black nav band.
    //
    // 🔴 Written here, once per state change, for exactly the reason
    // --pp-header-h is: BOTH terms animate during the shrink (row 3's `top`,
    // and its own height via `.header_nav_link`'s padding). Because all three
    // transitions are the same `.25s ease`, the sum of two interpolations on
    // one easing curve IS that curve between the summed endpoints —
    //     top(t) + height(t) = (a+c) + ((b+d) - (a+c)) * ease(t)
    // — so a consumer transitioning `top` on the same curve is in exact
    // lockstep with row 3's real bottom edge, with no JS running mid-flight.
    // Change the duration or easing anywhere in that chain and it breaks.
    var navH = scrolled ? heights.navShrunk : heights.navRest;
    if (navH == null) {
      var navEl = document.querySelector('.header_nav_section');
      navH = navEl ? navEl.getBoundingClientRect().height : 0;
    }
    root.style.setProperty('--pp-chrome-h', headerBottom + Math.floor(navH) + 'px');
  }

  function remeasure() {
    measureHeights();
    writeNavOffset();
  }

  // Guard: ignore height changes for the length of the shrink transition, so
  // nothing overwrites --pp-header-h while `top` is mid-flight (requirement 2).
  var settleTimer = null;

  function remeasureSettled() {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(remeasure, 300);
  }

  /* ---------------------------------------------------------------------- */

  bindScroll();
  remeasure();
  applyScrolled();

  SQUEEZE_QUERY.addEventListener('change', function () {
    bindScroll();
    applyScrolled();
    remeasure();
  });

  window.addEventListener('resize', remeasureSettled, { passive: true });

  // The header's resting height changes when the brand webfont swaps in, which
  // lands after this script runs.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
  window.addEventListener('load', remeasure);

  // Safety net for height changes that are not the scroll shrink — the menu
  // overflowing, the theme editor re-rendering the section. Debounced through
  // the same settle guard so it can never fire mid-transition.
  if (window.ResizeObserver) {
    var headerEl = document.querySelector('#header-component');
    if (headerEl) new ResizeObserver(remeasureSettled).observe(headerEl);
  }

  /* =========================================================================
   * 3. Tablet / mobile header (below 990px)
   *
   * Three things Horizon does not do on its own:
   *
   *   a) It picks inline-menu vs drawer from `data-menu-style`, which its own
   *      script sets from TOUCH CAPABILITY and available room — not viewport
   *      width (see horizon-migration.md §10). The design wants the drawer below
   *      990px regardless, so the attribute is forced here. Setting the attribute
   *      rather than just hiding things in CSS matters: Horizon renders TWO
   *      search actions and hides the wrong one off this same attribute, so
   *      faking it visually would show both.
   *
   *   b) The drawer toggle lives in `.header__column--left`. The design puts it
   *      to the RIGHT of the cart. Left and right columns are separate flex
   *      containers, so `order` cannot move it between them — the node has to be
   *      relocated. It is a self-contained <details>, so moving it is safe.
   *
   *   c) Row 3's categories are a separate section and are not in the drawer's
   *      link list at all. `sections/header-category-nav.liquid` renders a
   *      <details> accordion copy of them (`.header_nav_mobile`, `hidden`), which
   *      is moved into the drawer's <nav> here, after Horizon's own menu.
   *
   * All three reverse above 990px, so resizing across the breakpoint restores
   * the desktop header exactly.
   * ====================================================================== */
  var MOBILE_QUERY = window.matchMedia('(max-width: 989px)');

  // 🔴 DO NOT MOVE HORIZON'S DRAWER OR SEARCH NODES. Position them in CSS.
  //
  // The design wants the hamburger to the right of the cart. Horizon puts both
  // the drawer toggle and the search button in the LEFT column when
  // `data-menu-style` is `drawer`, so the obvious fix is to relocate the node.
  // Two rounds of that failed (2026-08-25) and the reasons are worth keeping:
  //
  //   * Moving the inner `<details>` breaks it — it is wired by its
  //     `<header-drawer>` custom-element PARENT via `on:click="/toggle"`, so
  //     lifted out it renders fine and does nothing.
  //   * Moving `<header-drawer>` itself keeps it working, but the drawer carries
  //     `data-skip-node-update="true"`: Horizon does not reconcile it on
  //     re-render, it just renders a fresh one in the original slot. The header
  //     ends up with TWO hamburgers, and which one is live depends on ordering.
  //     Measured at 932px: three drawer nodes across both columns.
  //
  // So the DOM is left exactly as Horizon builds it and `_header.scss` positions
  // the two strays into the right-hand cluster. Nothing to re-run, nothing to
  // fight, and a Horizon re-render simply repaints in the right place.
  //
  // What still has to happen in JS:
  //   a) `data-menu-style` — Horizon picks menu vs drawer from touch capability
  //      and available room, not viewport width, so the breakpoint is forced.
  //      Setting the attribute (rather than faking it in CSS) also keeps Horizon
  //      hiding the correct one of its two search actions.
  //   b) Row 3's categories are a separate section and are not in the drawer's
  //      link list at all, so the accordion copy rendered by
  //      `sections/header-category-nav.liquid` is appended into the drawer nav.

  // 🔴 Hold REFERENCES to the category <li>s; do not re-query them.
  //
  // Horizon re-renders the drawer's contents, which detaches anything appended
  // into it. Measured at 932px: after opening the drawer once,
  // `document.querySelector('.header_nav_mobile')` returned null — re-querying
  // in order to re-append cannot work, because there is nothing left to find.
  //
  // The nodes are only detached, not destroyed, so holding them in an array
  // means they can simply be put back. `placeCategoryItems()` therefore runs on
  // every drawer `toggle` as well as on the usual triggers.
  //
  // They are moved into Horizon's OWN `ul.menu-drawer__menu`, not appended as a
  // separate block after it, so the categories are list siblings of menu 1's
  // entries — which is what makes them share its dividers and spacing exactly.
  var catSource = document.querySelector('.header_nav_mobile');
  var catItems = catSource ? Array.prototype.slice.call(catSource.children) : [];

  function placeCategoryItems() {
    if (!catItems.length || !MOBILE_QUERY.matches) return;

    var list = document.querySelector('header-drawer .menu-drawer__navigation > .menu-drawer__menu');
    if (!list) return;

    for (var i = 0; i < catItems.length; i++) {
      if (catItems[i].parentElement !== list) list.appendChild(catItems[i]);
      catItems[i].hidden = false;
    }
    if (catSource) catSource.hidden = true;
  }

  function hideCategoryItems() {
    for (var i = 0; i < catItems.length; i++) catItems[i].hidden = true;
  }

  // The account link for the foot of the drawer. Held by reference for the same
  // reason as the category rows — Horizon re-renders the drawer's contents and
  // evicts anything appended into it.
  var drawerAccount = document.querySelector('.pp_drawer_account');

  function placeDrawerAccount() {
    if (!drawerAccount || !MOBILE_QUERY.matches) return;

    // `.menu-drawer__utility-links` is Horizon's own bottom-of-drawer slot
    // (`margin-block: auto …` pins it there). Fall back to the nav's parent if
    // the merchant has no localization selectors, in which case Horizon may not
    // render that wrapper at all.
    var slot = document.querySelector('header-drawer .menu-drawer__utility-links');
    if (!slot) {
      var nav = document.querySelector('header-drawer .menu-drawer__navigation');
      slot = nav && nav.parentElement;
    }
    if (!slot) return;

    if (drawerAccount.parentElement !== slot) slot.appendChild(drawerAccount);
    drawerAccount.hidden = false;
    document.documentElement.classList.add('pp_account_in_drawer');
  }

  function applyMobileHeader() {
    var header = document.querySelector('#header-component');

    if (MOBILE_QUERY.matches) {
      if (header) header.dataset.menuStyle = 'drawer';
      placeDrawerBits();
      watchDrawer();
    } else {
      hideCategoryItems();
      if (drawerAccount) drawerAccount.hidden = true;
      // Drop the flag so the header's account icon comes back above 990.
      document.documentElement.classList.remove('pp_account_in_drawer');
    }
  }

  // 🔴 PLACEMENT MUST SURVIVE LOSING THE RACE, NOT DEPEND ON WINNING IT.
  //
  // Reported 2026-09-09: the drawer "hides other menu option after certain
  // time" — intermittently it showed only menu 1's five links, with the SHOP
  // heading and every category row gone, and reopening it fixed itself.
  //
  // The cause was that `placeCategoryItems()` ran EXACTLY ONCE per toggle,
  // synchronously. Measured on a product page: at load the drawer list held
  // `rows: 5, cat: 0` and the rows only appeared on the first toggle — i.e. the
  // drawer's own `<ul>` is not always in the DOM when this fires. Lose that race
  // (a slow phone, a cold cache, Horizon re-rendering a frame later) and there
  // was NO retry, so that open stayed broken until the next one.
  //
  // ⚠️ It never reproduced here: this machine won the race in every run of
  // open / close / submenu / back / reopen / add-to-cart. An intermittent report
  // that a fast browser cannot reproduce is a race until proven otherwise —
  // making it idempotent is the fix, not hunting for the losing frame.
  function placeDrawerBits() {
    // Re-capture the source if it had not been parsed when this file ran. The
    // account link and the drawer <ul> both had a late-arrival retry already;
    // the category rows were the one held reference that did not.
    if (!catItems.length) {
      catSource = document.querySelector('.header_nav_mobile');
      if (catSource) catItems = Array.prototype.slice.call(catSource.children);
    }
    placeCategoryItems();
    placeDrawerAccount();
  }

  // Idempotent, so retrying costs nothing: `placeCategoryItems()` only appends a
  // node whose `parentElement` is already wrong, so a re-run that finds
  // everything in place writes nothing and emits no mutation record.
  function placeDrawerBitsSoon() {
    placeDrawerBits();
    requestAnimationFrame(placeDrawerBits);
    setTimeout(placeDrawerBits, 120);
    setTimeout(placeDrawerBits, 400);
  }

  // The drawer fires `toggle` on its <details> whenever it opens or closes —
  // the reliable moment to check the items are still in there. Capture phase:
  // `toggle` does not bubble.
  document.addEventListener('toggle', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('menu-drawer-container')) {
      placeDrawerBitsSoon();
    }
  }, true);

  // 🔴 AND A WATCH ON THE DRAWER ITSELF, because a re-render can evict the rows
  // at a moment no toggle fires at all.
  //
  // ⚠️ DISCONNECTED FOR THE DURATION OF OUR OWN WRITES. This file has already
  // hung a tab once (the Judge.me `bleed()` loop): an observer that watches the
  // subtree it also writes to will re-enter itself for ever. The re-check before
  // writing is a second guard, not the only one.
  var drawerObserver = null;
  function watchDrawer() {
    if (drawerObserver || typeof MutationObserver !== 'function') return;
    var host = document.querySelector('header-drawer');
    if (!host) return;

    drawerObserver = new MutationObserver(function () {
      if (!MOBILE_QUERY.matches || !catItems.length) return;

      var list = document.querySelector('header-drawer .menu-drawer__navigation > .menu-drawer__menu');
      if (!list) return;

      // Only act when something is ACTUALLY out of place — otherwise every
      // mutation Horizon makes would trigger a write.
      var evicted = false;
      for (var i = 0; i < catItems.length; i++) {
        if (catItems[i].parentElement !== list) { evicted = true; break; }
      }
      if (!evicted) return;

      drawerObserver.disconnect();
      placeDrawerBits();
      drawerObserver.observe(host, { childList: true, subtree: true });
    });

    drawerObserver.observe(host, { childList: true, subtree: true });
  }

  applyMobileHeader();
  watchDrawer();
  MOBILE_QUERY.addEventListener('change', applyMobileHeader);

  // Horizon's own script re-decides `data-menu-style` on resize, so re-assert.
  window.addEventListener('resize', applyMobileHeader, { passive: true });

  // The drawer's <nav> is rendered by a theme block, so it can arrive after this
  // script runs; re-run once the header is complete.
  window.addEventListener('load', applyMobileHeader);
})();

// ---------------------------------------------------------------------------
// Branded video play / pause — snippets/home-media.liquid
//
// Global rather than inline in a section, because the media snippet is shared:
// two copies of this would be two behaviours to keep in sync the first time one
// of them is touched.
//
// 🔴 DELEGATED, not bound per element. Sections are re-rendered by the theme
// editor on every settings change, which throws away directly-bound listeners —
// the button then looks fine and does nothing, which is the worst kind of bug to
// notice. One listener on `document` survives every re-render.
// ---------------------------------------------------------------------------
(function () {
  function syncState(wrap, video) {
    var playing = !video.paused && !video.ended;
    wrap.classList.toggle('is_playing', playing);

    var btn = wrap.querySelector('.pp_video_toggle');
    if (!btn) return;
    btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    // The label has to follow the icon — a button that shows a pause glyph
    // while announcing "Play video" is worse than no label at all.
    var label = playing ? btn.dataset.labelPause : btn.dataset.labelPlay;
    if (label) btn.setAttribute('aria-label', label);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.pp_video_toggle');
    if (!btn) return;

    var wrap = btn.closest('.pp_media_video');
    var video = wrap && wrap.querySelector('video');
    if (!video) return;

    e.preventDefault();

    if (video.paused || video.ended) {
      // `play()` returns a promise that REJECTS when the browser blocks it —
      // an unmuted video with no user gesture, or a low-power-mode phone. Left
      // unhandled that is an uncaught rejection in the console and a button
      // stuck showing "pause" over a video that never started.
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () { syncState(wrap, video); });
      }
    } else {
      video.pause();
    }
  });

  // Drive the state off the VIDEO's own events, not off the click. Native
  // controls, the keyboard, picture-in-picture and the end of a non-looping clip
  // all change playback without going through our button.
  ['play', 'pause', 'ended'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var video = e.target;
      if (!video || video.tagName !== 'VIDEO') return;
      var wrap = video.closest && video.closest('.pp_media_video');
      if (wrap) syncState(wrap, video);
    }, true); // capture — media events do not bubble
  });
})();

// ---------------------------------------------------------------------------
// Reveal animations — assets/_animations.scss
//
// One observer for the whole site. Sections opt in purely from markup:
//
//   <h2 data-reveal="bottom" data-reveal-delay="1">
//
// `data-reveal` names the edge the element comes FROM. Nothing to register, no
// per-section script — which is the point: the next section that wants a reveal
// adds an attribute, not a file.
// ---------------------------------------------------------------------------
(function () {
  var SEL = '[data-reveal]:not(.is_revealed)';

  function revealAll(root) {
    (root || document).querySelectorAll(SEL).forEach(function (el) {
      el.classList.add('is_revealed');
    });
  }

  // No observer support, or the visitor has asked for less motion: show
  // everything immediately. Content is never allowed to depend on the animation
  // having run.
  if (!('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealAll();
    document.addEventListener('shopify:section:load', function (e) { revealAll(e.target); });
    // 🔴 Exposed for markup injected AFTER load — see the note on `scan` below.
    window.ppRevealScan = function (root) { revealAll(root); };
    return;
  }

  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is_revealed');
        // Once only. A reveal that replays every time you scroll past reads as a
        // glitch, and re-observing costs main-thread work for nothing.
        io.unobserve(entry.target);
      });
    },
    {
      // Fire a little BEFORE the element reaches the bottom edge, so the motion
      // is finishing as it settles into view rather than starting there.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  function scan(root) {
    (root || document).querySelectorAll(SEL).forEach(function (el) { io.observe(el); });
  }

  // 🔴 EXPOSED, because anything injected after load is invisible otherwise.
  // `[data-reveal]` starts at `opacity: 0` and is only ever un-hidden by this
  // observer — and the observer is told about elements once, at scan time. So a
  // card appended later (collection Load More, a fetched product tab) is in the
  // DOM, occupies its grid cell, and renders as BLANK WHITE SPACE forever.
  // ⚠️ That failure is invisible to a test that counts nodes: the cards are all
  // there. Assert on `opacity` / `.is_revealed`, not on `children.length`.
  window.ppRevealScan = scan;

  scan();

  // The theme editor re-renders a whole section on every settings change, which
  // hands back fresh nodes the observer has never seen. Without this they stay
  // at opacity 0 and the merchant thinks the section broke.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();

// ---------------------------------------------------------------------------
// YouTube / Vimeo facade — snippets/home-media.liquid
//
// The section renders a poster image and a real <button>; the iframe does not
// exist until someone asks for the video.
//
// 🔴 Why a facade at all: a YouTube embed costs ~700KB-1MB and dozens of
// requests on PAGE LOAD, whether or not anyone presses play. The GEO brief for
// this store counts payload as an SEO cost, so an embed nobody watches must not
// be paid for by everybody.
//
// 🔴 DELEGATED, like the video toggle above — the theme editor re-renders a
// whole section on every settings change and throws away directly-bound
// listeners, leaving a button that looks right and does nothing.
// ---------------------------------------------------------------------------
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.pp_embed_start');
    if (!btn) return;

    var wrap = btn.closest('.pp_media_embed');
    if (!wrap) return;

    var src = wrap.getAttribute('data-embed-src');
    if (!src) return;

    var frame = document.createElement('iframe');
    frame.src = src;
    // A title is required for a screen reader to say anything useful about an
    // iframe; without it the whole embed is announced as "frame".
    frame.title = wrap.getAttribute('data-embed-title') || 'Video';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    frame.loading = 'lazy';

    wrap.replaceChildren(frame);
    // The click was a deliberate request to watch — move focus to the player so
    // a keyboard user is not left on a button that no longer exists.
    frame.focus({ preventScroll: true });
  });
})();

// ---------------------------------------------------------------------------
// Provider player finished loading — snippets/home-media.liquid
//
// The loading ring sits BEHIND the iframe and is covered when the player paints,
// so this is not what makes it disappear in the normal case. It exists for the
// abnormal one: if the embed is slow, blocked or fails, the ring would otherwise
// spin forever behind a transparent iframe.
//
// 🔴 Capture phase — `load` does not bubble.
// ---------------------------------------------------------------------------
(function () {
  document.addEventListener('load', function (e) {
    var el = e.target;
    if (!el || el.tagName !== 'IFRAME') return;
    var wrap = el.closest && el.closest('.pp_media_embed');
    if (wrap) wrap.classList.add('is_loaded');
  }, true);
})();

// ---------------------------------------------------------------------------
// Team bio "Read more" — below 550px only
//
// Two jobs: toggle the clamp, and decide whether the control should exist at all.
//
// 🔴 The button is only revealed where the bio ACTUALLY overflows. Some of these
// bios are four lines and some are fifteen; a "Read more" on a short one is a
// control that does nothing, which is worse than no control. Measured against the
// clamped height, and re-measured on resize because the line count changes with
// the width.
// ---------------------------------------------------------------------------
(function () {
  var QUERY = window.matchMedia('(max-width: 550px)');

  function audit() {
    document.querySelectorAll('[data-team-more]').forEach(function (btn) {
      var member = btn.closest('.team_member');
      // 🔴 Measure the WRAPPER — it is the element doing the clipping.
      //
      // ⚠️ This has to follow whatever is clamping. While the bio itself was
      // line-clamped the wrapper stopped overflowing (`scrollHeight ===
      // clientHeight`) and the button vanished on every card; measuring the bio
      // fixed that, and measuring the bio would fail the same way now that the
      // clamp is back on the wrapper alone. Ask whichever element clips.
      var wrap = member && member.querySelector('[data-team-bio]');
      if (!wrap) return;

      if (!QUERY.matches) {
        // Leave nothing behind above the breakpoint — the bio is shown in full
        // there, and a stale `is_expanded` would be harmless but confusing.
        btn.classList.remove('is_needed');
        member.classList.remove('is_expanded');
        btn.setAttribute('aria-expanded', 'false');
        return;
      }
      if (member.classList.contains('is_expanded')) { btn.classList.add('is_needed'); return; }
      // A couple of pixels of slack: sub-pixel line heights make an exactly-fitting
      // bio report a 1px overflow, which would show the control for nothing.
      btn.classList.toggle('is_needed', wrap.scrollHeight > wrap.clientHeight + 4);
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-team-more]');
    if (!btn) return;
    var member = btn.closest('.team_member');
    if (!member) return;
    var open = member.classList.toggle('is_expanded');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
  });

  var t;
  window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(audit, 200); }, { passive: true });
  window.addEventListener('load', audit);
  document.addEventListener('shopify:section:load', audit);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(audit);
  audit();
})();

// ---------------------------------------------------------------------------
// Policy pages — give every table its own horizontal scroller
//
// The rates table on Shipping Deadlines is five columns wide and cannot fit a
// phone. Without this it forces the whole PAGE to scroll sideways, which breaks
// every other section on the page rather than just the table.
//
// 🔴 It has to be done in JS. The markup comes from `{{ page.content }}` — the
// merchant's rich text — so there is no Liquid of ours around it to add a
// wrapper to, and CSS alone cannot introduce a scroll container without
// `display: block` on the table, which destroys the table layout.
//
// `tabIndex = 0` is not decoration: a scroll container that cannot be focused
// cannot be scrolled by keyboard, which fails WCAG 2.1.1 for anyone who reaches
// the table without a mouse.
// ---------------------------------------------------------------------------
(function () {
  function wrapTables() {
    var tables = document.querySelectorAll('.policy_body table');
    for (var i = 0; i < tables.length; i++) {
      var table = tables[i];
      if (table.parentNode && table.parentNode.classList.contains('policy_table_wrap')) continue;
      var wrap = document.createElement('div');
      wrap.className = 'policy_table_wrap';
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('tabindex', '0');
      var caption = table.querySelector('caption');
      wrap.setAttribute('aria-label', caption ? caption.textContent.trim() : 'Table');
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    }
  }

  document.addEventListener('DOMContentLoaded', wrapTables);
  document.addEventListener('shopify:section:load', wrapTables);
  wrapTables();
})();


// ---------------------------------------------------------------------------
// TAB STRIPS — the shared overflow affordance
//
// Every `[data-pp-tab-bar]` on the store: the FAQ page's category anchors and
// home sections 2/4's product-collection buttons. The strip's LOOK is
// assets/_tabs.scss; this is the part that decides whether the "swipe for more"
// hint and the edge fades are shown at all.
//
// 🔴 Promoted here rather than copied into the second section — a hint that
// works on one tab strip and not the other is exactly the kind of drift nobody
// notices one page at a time.
//
// 🔴 The state is deliberately split in two, because only one half is safe to
// run while a bar is pinned:
//
//   has_more          the strip overflows at all. Adding it changes the bar's
//                     HEIGHT, which on a sticky bar shoves every row below it —
//                     so it is recomputed on resize and on a content change,
//                     and NEVER on scroll.
//   at_start/at_end   which end we are at. Opacity only, no layout, so it is
//                     safe on every scroll frame.
//
// 🔴 TWO observers, because neither covers the other (measured 2026-08-31):
//
//   ResizeObserver    the strip's own BOX changed — a percentage column can
//                     move with no window resize.
//   MutationObserver  its CONTENT changed. A ResizeObserver CANNOT see this:
//                     adding a tab grows scrollWidth while the border box, the
//                     only thing an RO watches, stays exactly the same.
//                     Verified — eight tabs injected at 1600px overflowed by
//                     741px and the RO never fired once.
//
// It also publishes `--pp-tab-bar-h` on the closest section, for anything that
// has to clear the bar (the FAQ groups' `scroll-margin-top`).
// ---------------------------------------------------------------------------
(function () {
  function initTabBar(bar) {
    if (!bar || bar.ppTabsInit) return;
    bar.ppTabsInit = true;

    var strip = bar.querySelector('[data-pp-tabs]');
    if (!strip) return;
    var host = bar.closest('.shopify-section') || bar;

    function overflowAmount() {
      return strip.scrollWidth - strip.clientWidth;
    }

    function writeBarHeight() {
      host.style.setProperty('--pp-tab-bar-h', Math.round(bar.getBoundingClientRect().height) + 'px');
    }

    function syncEnds() {
      var max = overflowAmount();
      bar.classList.toggle('at_end', max > 1 && strip.scrollLeft >= max - 1);
      bar.classList.toggle('at_start', strip.scrollLeft <= 1);
    }

    function syncHasMore() {
      bar.classList.toggle('has_more', overflowAmount() > 1);
      syncEnds();
      writeBarHeight();
    }

    syncHasMore();
    strip.addEventListener('scroll', syncEnds, { passive: true });
    window.addEventListener('resize', syncHasMore, { passive: true });

    if (window.ResizeObserver) {
      new ResizeObserver(syncHasMore).observe(strip);
      new ResizeObserver(writeBarHeight).observe(bar);
    }
    if (window.MutationObserver) {
      // `characterData` + `subtree` so a RENAMED tab counts too.
      new MutationObserver(syncHasMore).observe(strip, {
        childList: true, subtree: true, characterData: true
      });
    }

    // The brand webfont swaps in after this script runs and changes every
    // tab's width — the same reason the header re-measures on it.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncHasMore);

    // Keep the ACTIVE pill in view. On a strip narrow enough to scroll, an
    // active tab off the right-hand edge reads as nothing having happened.
    bar.ppScrollActiveIntoView = function (el) {
      if (!el || overflowAmount() < 1) return;
      var s = strip.getBoundingClientRect(), e = el.getBoundingClientRect();
      if (e.left >= s.left && e.right <= s.right) return;
      strip.scrollTo({
        left: strip.scrollLeft + (e.left - s.left) - (s.width - e.width) / 2,
        behavior: 'smooth'
      });
    };
  }

  function initAll() {
    var bars = document.querySelectorAll('[data-pp-tab-bar]');
    for (var i = 0; i < bars.length; i++) initTabBar(bars[i]);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  initAll();
})();


// ---------------------------------------------------------------------------
// GOOGLE MAP — contact section 3
//
// 🔴 The JS Maps API rather than an embed iframe, because the design is a
// DESATURATED map with a BRANDED RED PIN and a `/maps/embed?pb=` URL can give
// neither. Same API the old Peter & Pauls store used.
//
// 🔴 Loaded ON SCROLL, never on page load. Google's maps bundle is ~300KB+ of
// third-party payload and this section sits at the very bottom of the contact
// page — the GEO brief for this store counts payload as an SEO cost, so
// somebody who never scrolls that far never pays for it. Same reasoning as the
// YouTube facade.
//
// ⚠️ The API key is a SECTION SETTING read off the element, never committed to
// the theme. With no key the fallback image simply stays, which is why the
// markup renders it underneath rather than swapping it in on failure.
// ---------------------------------------------------------------------------
(function () {
  var loading = false;
  var pending = [];

  function styledOptions(el, libs) {
    var lat = parseFloat(el.dataset.mapLat);
    var lng = parseFloat(el.dataset.mapLng);

    // The design puts the zoom control at the map's BOTTOM-LEFT; Google's
    // default is bottom-right, where it collides with the address card.
    // ⚠️ `ControlPosition` is an enum object, not a constructor, and it lives in
    // the 'core' library — so it is read with `pick`, not `ctor`.
    var cp = pick(libs, 'ControlPosition');

    var opts = {
      center: { lat: lat, lng: lng },
      zoom: parseInt(el.dataset.mapZoom, 10) || 14,
      disableDefaultUI: true,
      zoomControl: true,
      // The design's map is desaturated so the red pin and the white card are
      // the only things carrying colour.
      styles: [
        { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 20 }] },
        { elementType: 'labels.text.fill', stylers: [{ saturation: -100 }, { lightness: -25 }] },
        { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        { featureType: 'water', stylers: [{ saturation: -100 }, { lightness: 45 }] }
      ]
    };

    if (cp && cp.LEFT_BOTTOM != null) opts.zoomControlOptions = { position: cp.LEFT_BOTTOM };
    return opts;
  }

  // 🔴 `loading=async` means the CONSTRUCTORS are not ready at script onload.
  //
  // The modern bootstrap loads its library set lazily, so the script's `load`
  // event only means "the loader is ready". `new google.maps.Map()` there throws
  // `google.maps.Map is not a constructor`, the section stays a grey box, and
  // it looks exactly like a rejected API key. (It is not: an auth failure calls
  // `gm_authFailure` and prints RefererNotAllowedMapError. Check the console for
  // which one you actually have before touching anything in Google Cloud.)
  //
  // 🔴 AND `typeof google.maps.Map` IS ALREADY 'function' AT THAT POINT.
  // The bootstrap installs a lazy STUB under that name, so a
  // `typeof … !== 'function'` guard skips the import and constructs the stub —
  // which throws the identical error. There is no cheap way to ask "is this the
  // real one"; the only correct answer is to ALWAYS await importLibrary.
  // 🔴 AND THE CONSTRUCTORS COME BACK IN THE RESOLVED OBJECT — they are never
  // patched onto `google.maps`. Awaiting importLibrary and then calling
  // `new google.maps.Map()` throws the SAME "is not a constructor", because
  // that name is still the stub. Google's own docs destructure the result
  // (`const { Map } = await google.maps.importLibrary('maps')`); this is the
  // part that is easy to read past, and it cost three deploys.
  //
  // `Size` and `Point` live in the 'core' library, not 'maps', so both are
  // imported and merged — with the namespace kept as a fallback for the legacy
  // loader, where the constructors really are on `google.maps`.
  // `Size` and `Point` live in the 'core' library, not 'maps', so both are
  // imported. They are kept as an ARRAY and read by direct property access.
  //
  // 🔴 Do NOT try to merge them into one object with `for...in`. The library
  // objects expose their members NON-ENUMERABLY, so the merge comes back empty
  // and every lookup falls through to the namespace stub — which throws the
  // very same "is not a constructor" one layer further along. Direct property
  // access reads them fine; enumeration does not.
  // 🔴 …AND `importLibrary` ITSELF IS NOT ATTACHED AT `onload`.
  //
  // This is the one that actually kept the map grey. The guard below reads "no
  // importLibrary means the old loader, so the constructors are on the
  // namespace" — true for the legacy bootstrap, and FALSE for a fraction of a
  // second after the async one loads: `google.maps` exists, `importLibrary`
  // does not yet, so it resolved instantly with the namespace STUB and threw.
  //
  // The signal to use is Google's own `callback=` parameter, which is what
  // `loading=async` expects and which does not fire until the API is genuinely
  // ready. `onload` only means the file arrived. See loadApi below.
  var mapsLib = null;
  function mapsReady() {
    if (!google.maps.importLibrary) return Promise.resolve([google.maps]);   // legacy loader
    if (!mapsLib) {
      mapsLib = Promise.all([
        google.maps.importLibrary('maps'),
        google.maps.importLibrary('core')
      ]);
    }
    return mapsLib;
  }

  // Any member (enum objects included) — `ctor` insists on a function, which an
  // enum like ControlPosition is not.
  function pick(libs, name) {
    if (libs) {
      for (var i = 0; i < libs.length; i++) {
        if (libs[i] && libs[i][name] != null) return libs[i][name];
      }
    }
    return google.maps[name];
  }

  // First library that actually has it wins; the namespace is the last resort.
  function ctor(libs, name) {
    if (libs) {
      for (var i = 0; i < libs.length; i++) {
        if (libs[i] && typeof libs[i][name] === 'function') return libs[i][name];
      }
    }
    return google.maps[name];
  }

  function draw(el) {
    if (!window.google || !window.google.maps || el.ppMapDrawn) return;
    var lat = parseFloat(el.dataset.mapLat);
    var lng = parseFloat(el.dataset.mapLng);
    if (isNaN(lat) || isNaN(lng)) return;

    el.ppMapDrawn = true;
    mapsReady().then(function (libs) { build(el, lat, lng, libs); }, function (e) {
      el.ppMapDrawn = false;      // leave the fallback image in place
      fail('importLibrary rejected', e);
    });
  }

  // 🔴 A swallowed failure is the expensive kind. The try/catch below exists so
  // a Maps problem cannot take the rest of custom.js down — but catching it
  // silently turned a NAMED error into an empty grey box with a clean console,
  // which is strictly harder to diagnose than the crash it replaced. Say what
  // happened, once, and leave the fallback image in place.
  function fail(what, e) {
    if (window.console && console.warn) {
      console.warn('[pp-map] ' + what + ':', (e && (e.message || e)) || '(no detail)');
    }
  }

  // Google calls this on an auth failure — a rejected key, a referrer that is
  // not on the key's allow-list, billing off. Defining it is what turns
  // "the map is grey" into a sentence.
  window.gm_authFailure = function () {
    fail('Google rejected the API key for this domain (gm_authFailure)',
      'add this store to the key HTTP-referrer allow-list in Google Cloud');
  };

  function build(el, lat, lng, libs) {
    var map;
    try {
      map = new (ctor(libs, 'Map'))(el, styledOptions(el, libs));
    } catch (e) {
      el.ppMapDrawn = false;
      fail('new google.maps.Map threw', e);
      return;
    }

    // The brand pin, inline rather than an asset — one less request, and it
    // takes the brand red from the same place every other control does.
    // 🔴 The designer's own paths, VERBATIM from Figma node 460:5736 (and
    // confirmed by Simranjeet's own export) — not a teardrop drawn by hand.
    // Two earlier attempts were estimated off a screenshot and both were wrong:
    // the frame says 44 x 66, and the white centre is r=14.245 at (22,22), i.e.
    // **65% of the pin's width**. That last number is the whole character of
    // the mark — it reads as a red RING, not a blob with a dot in it, and at
    // 36% it was simply a different icon.
    //
    // ⚠️ All three subpaths are kept even though the middle one is covered by
    // the third. Redrawing "the same shape more simply" is how the first two
    // attempts went wrong; the export is the spec.
    var pin = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="66" viewBox="0 0 44 66" fill="none">' +
      '<path d="M22 0C10.45 0 0 9.35 0 20.9C0 32.45 9.5425 46.2275 22 66C34.4575 46.2275 44 32.45 44 20.9C44 9.35 33.55 0 22 0ZM22 38.5C12.8975 38.5 5.5 31.1025 5.5 22C5.5 12.8975 12.8975 5.5 22 5.5C31.1025 5.5 38.5 12.8975 38.5 22C38.5 31.1025 31.1025 38.5 22 38.5Z" fill="#EE1C21"/>' +
      '<path d="M22.0006 39.2975C31.5538 39.2975 39.2981 31.5532 39.2981 22C39.2981 12.4469 31.5538 4.70251 22.0006 4.70251C12.4475 4.70251 4.70312 12.4469 4.70312 22C4.70312 31.5532 12.4475 39.2975 22.0006 39.2975Z" fill="#EE1C21"/>' +
      '<path d="M22.0028 36.245C29.8701 36.245 36.2478 29.8673 36.2478 22C36.2478 14.1327 29.8701 7.755 22.0028 7.755C14.1355 7.755 7.75781 14.1327 7.75781 22C7.75781 29.8673 14.1355 36.245 22.0028 36.245Z" fill="white"/></svg>'
    );

    // The marker is its own try: google.maps.Marker is deprecated, and if a
    // future release removes it the MAP should still be there.
    try {
      // Pin scale (2026-09-01, Simranjeet): the artwork's own 44 x 66 read too
      // large on the map, so -25% everywhere and a further -20% on phones,
      // where the map box itself is much smaller.
      var Size = ctor(libs, 'Size'), Point = ctor(libs, 'Point');
      var phone = window.matchMedia('(max-width: 749px)');

      function icon() {
        var k = 0.75 * (phone.matches ? 0.8 : 1);
        return {
          url: pin,
          // 🔴 The anchor is the TIP, not the centre — the path ends at
          // (22, 66) — and it has to scale WITH the size or the pin stops
          // pointing at its own coordinate the moment the scale changes.
          scaledSize: new Size(44 * k, 66 * k),
          anchor: new Point(22 * k, 66 * k)
        };
      }

      var marker = new (ctor(libs, 'Marker'))({
        position: { lat: lat, lng: lng },
        map: map,
        title: el.dataset.mapQuery || '',
        icon: icon()
      });

      // The marker is built once, so the scale has to be re-applied when the
      // breakpoint is crossed — same rule as everything else on this build.
      phone.addEventListener('change', function () { marker.setIcon(icon()); });
    } catch (e) {
      fail('marker failed (the map itself is fine)', e);
    }

    offsetCentre(el, map, libs, lat, lng);
    el.classList.add('is_loaded');
  }

  // 🔴 The design does NOT centre the pin: Figma puts it 21% across the map
  // (node 460:5736 at x1147 + half its 44 width = 1169, in a map spanning
  // 960 -> 1954), because the address card sits over the right-hand side. The
  // marker still points at the true coordinate; it is the VIEW that shifts.
  //
  // ⚠️ Done by moving the centre through the projection, not with `panBy()`.
  // panBy animates whenever the distance is under one viewport, so the map
  // would visibly slide sideways the moment it appears — which reads as a bug.
  function offsetCentre(el, map, libs, lat, lng) {
    var LatLng = ctor(libs, 'LatLng'), Point = ctor(libs, 'Point');
    var stacked = window.matchMedia('(max-width: 989px)');

    function pinFraction() {
      // 🔴 CENTRED below 990. The offset exists only to keep the pin clear of
      // the address card, and below that breakpoint the card has moved out from
      // over the map entirely — so there is nothing left to dodge.
      if (stacked.matches) return 50;
      var v = parseFloat(el.dataset.mapPinX);
      return isNaN(v) ? 21 : v;
    }

    function place() {
      var proj = map.getProjection();    // only exists once the map is idle
      if (!proj) return;

      var dx = el.getBoundingClientRect().width * (0.5 - pinFraction() / 100);

      // 🔴 Always measured from the PIN, never from the map's current centre.
      // Reading the centre makes the calculation cumulative, so every re-run —
      // a breakpoint change, a resize — would push the map further sideways.
      // From the pin it is idempotent: run it as often as you like.
      var scale = Math.pow(2, map.getZoom());
      var wp = proj.fromLatLngToPoint(new LatLng(lat, lng));
      map.setCenter(dx ? proj.fromPointToLatLng(new Point(wp.x + dx / scale, wp.y))
                       : new LatLng(lat, lng));
    }

    var once = map.addListener('idle', function () {
      once.remove();          // setCenter fires 'idle' again
      place();
    });

    // The map is built once, so the crossing has to re-place it.
    stacked.addEventListener('change', place);
  }

  function loadApi(key) {
    if (loading) return;
    loading = true;
    // 🔴 `callback=` is the ONLY reliable "the API is ready" signal under
    // `loading=async`. `onload` fires when the FILE arrives, which is before
    // `google.maps.importLibrary` is attached and long before the constructors
    // are real — draw from there and it throws "is not a constructor".
    window.ppMapsReady = function () { pending.splice(0).forEach(draw); };

    var s = document.createElement('script');
    s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
            '&loading=async&v=weekly&callback=ppMapsReady';
    s.async = true;
    // A blocked, wrong or over-quota key must leave the fallback image in
    // place, not an empty box — so there is nothing to undo here.
    s.onerror = function () { loading = false; fail('the Maps script did not load', s.src.split('?')[0]); };
    document.head.appendChild(s);
  }

  function arm(el) {
    var key = el.dataset.mapKey;
    if (!key) return;                    // no key: the fallback image stays
    pending.push(el);
    if (window.google && window.google.maps) { draw(el); return; }
    loadApi(key);
  }

  function initMaps() {
    var maps = document.querySelectorAll('[data-pp-map]:not([data-pp-map-armed])');
    for (var i = 0; i < maps.length; i++) {
      (function (el) {
        el.setAttribute('data-pp-map-armed', '');
        if (!('IntersectionObserver' in window)) { arm(el); return; }
        var io = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          arm(el);
        }, { rootMargin: '300px' });   // start loading just before it is needed
        io.observe(el);
      })(maps[i]);
    }
  }

  document.addEventListener('DOMContentLoaded', initMaps);
  document.addEventListener('shopify:section:load', initMaps);
  initMaps();
})();


/* ===========================================================================
   COLLECTION SECTION 2 — the toolbar, the drawer and Load More
   ---------------------------------------------------------------------------
   Three behaviours, none of which re-implements anything Horizon already does:

     1. the column tabs   — write one custom property, remember the choice
     2. the filter drawer — open Horizon's own <dialog-component>
     3. Load More         — REVEAL Horizon's next-page sentinel

   🔴 (3) is the one worth understanding. `paginated-list.js` fetches and appends
   the next page from an IntersectionObserver watching `[ref="viewMoreNext"]`.
   A `display: none` element has no box, so it can never intersect — which is
   how "button" mode is expressed in CSS alone. Clicking the button shows the
   sentinel, it intersects immediately, Horizon renders the next page, and the
   sentinel is hidden again. Infinite scroll is the SAME code path with the
   sentinel simply never hidden. No second implementation, no fetching of ours.

   🔴 EVERYTHING HERE IS DELEGATED, AND NOTHING HOLDS A NODE REFERENCE.
   A filter change re-renders this section through the Section Rendering API,
   which `section-renderer.js` applies with `morph()`. Measured, rather than
   assumed: the section element survives, but the <dialog> and the Load More
   button are REPLACED. So anything bound to a captured node is silently lost
   after the first filter tick — which is exactly how the drawer stopped
   re-opening and Load More stopped responding, with no error in the console.

   Two further things a morph wipes, each of which was its own bug:
     • an "armed" marker written as a DOM attribute — so re-arming double-binds
       every listener that DID survive. There is no marker any more; delegation
       needs none.
     • an inline `--coll-cols` on the grid — so the visitor's column choice
       silently reverted on every filter tick. It is written on <html> instead,
       which is outside the section. (Horizon hits the same wall and solves it
       by listing `product-grid-view` in morph.js's hardcoded
       PRESERVED_ATTRIBUTES — an array we would have to fork to add to.)
   =========================================================================== */
(function () {
  var COLS_KEY = 'pp-collection-cols';

  function section() { return document.querySelector('.coll_sec_2'); }
  function drawer()  { var s = section(); return s && s.querySelector('.coll_drawer'); }
  function host()    { var s = section(); return s && s.querySelector('[data-coll-filter-drawer]'); }

  /* ---- 1. the column tabs ------------------------------------------------ */
  function paintCols(value) {
    document.documentElement.style.setProperty('--pp-coll-cols', value);
    var buttons = document.querySelectorAll('.coll_cols_btn');
    for (var i = 0; i < buttons.length; i++) {
      var on = buttons[i].getAttribute('data-cols') === value;
      buttons[i].classList.toggle('is_active', on);
      buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  function restoreCols() {
    // ⚠️ Wrapped: Safari in private mode THROWS on sessionStorage rather than
    // returning null, and an exception here would take the tabs down with it.
    var stored = null;
    try { stored = sessionStorage.getItem(COLS_KEY); } catch (e) {}
    if (stored && document.querySelector('.coll_cols_btn[data-cols="' + stored + '"]')) paintCols(stored);
  }

  /* ---- 2. the drawer ----------------------------------------------------- */
  // True only while a filter change is in flight — it is what tells the code
  // below that a vanished drawer is a re-render and not the visitor.
  var reRendering = false;

  // 🔴 SORT BY is the only group open when the drawer opens; every FILTER group
  // starts collapsed. Horizon renders all of its panels open, which fills the
  // drawer with a wall of checkboxes and buries everything below the fold.
  //
  // ⚠️ "The first tab" means Sort By, which is ours and is `<details open>` in
  // the markup — the filter panels below it are Horizon's. An earlier version
  // kept `i === 0` open here, which looked right only while Availability was the
  // first filter; once that was switched off in Search & Discovery, Price became
  // index 0 and opened instead. Counting by index was the mistake.
  //
  // ⚠️ A group holding an ACTIVE value stays open regardless — collapsing a
  // filter the visitor has already applied hides the fact that it is on.
  function collapseAllButFirst() {
    var d = drawer();
    if (!d) return;
    var panels = d.querySelectorAll('.coll_facets .facets__panel');
    for (var i = 0; i < panels.length; i++) {
      panels[i].open = !!panels[i].querySelector('input:checked');
    }
  }

  function openDrawer() {
    var h = host(), d = drawer();
    if (!h || !d || d.open) return;
    try {
      if (typeof h.showDialog === 'function') h.showDialog();
      else d.showModal();
    } catch (e) {
      // 🔴 Never swallow this. A stale dialog left in the top layer throws
      // InvalidStateError here, and silence turned that into "the drawer just
      // does not re-open" — three rounds of guessing before it was logged.
      console.warn('[pp] collection drawer could not open:', e);
      return;
    }
    var btn = document.querySelector('[data-coll-filter-open]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    collapseAllButFirst();
  }

  // 🔴 Driven off the CHANGE, not off `collection:update`. That event comes from
  // Shopify's external standard-events bundle and is dispatched on a component
  // INSIDE the element being morphed, so neither its bubbling nor its timing
  // relative to the DOM swap is ours to rely on — hanging the re-open off it
  // simply never fired. Polling for the swap is self-contained and provable.
  //
  // The morph REPLACES the dialog (measured), so "it came back" means either a
  // different node in its place, or the same node with `open` stripped. Both
  // are handled; anything else keeps waiting until the budget runs out.
  var watching = null;

  function reopenAfterRender(previous, tries) {
    if (!reRendering) { document.documentElement.classList.remove('pp_facet_rerender'); return; }
    var d = drawer();
    if (d && (d !== previous || !d.open)) {
      reRendering = false;
      openDrawer();
      // Let the swap paint before the slide transition is allowed back, or the
      // re-opened panel animates in from the left and reads as a close+open.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          document.documentElement.classList.remove('pp_facet_rerender');
        });
      });
      return;
    }
    if ((tries || 0) > 240) {
      reRendering = false;
      document.documentElement.classList.remove('pp_facet_rerender');
      return;
    }
    watching = requestAnimationFrame(function () { reopenAfterRender(previous, (tries || 0) + 1); });
  }

  // 🔴 A MORPH STRIPS `is_revealed`, SO EVERY CARD COMES BACK INVISIBLE.
  // `[data-reveal]` starts at `opacity: 0`; the class that un-hides it is not in
  // the re-rendered HTML, and the observer was only ever told about the OLD
  // nodes. So after a filter or sort change the grid was correctly populated and
  // rendered as a blank white page — which is exactly what a page reload fixed.
  //
  // Polled rather than hooked to `collection:update`: that event comes from
  // Shopify's external bundle and fires before the DOM swap. The selector is
  // `[data-reveal]:not(.is_revealed)`, so once everything is revealed each pass
  // matches nothing and costs nothing.
  var revealTimer = null;
  var swapTimer = null;

  // 🔴 Hold the OLD grid, dimmed, until the new images have actually PAINTED.
  //
  // Three attempts, and the first two both released too early — recording them
  // because each looked correct and the flash survived untouched:
  //
  //   1. "images near the viewport are complete" — at the moment of the click
  //      the OUTGOING images are all complete, so the hold ended instantly.
  //      Measured: the dim lasted zero frames.
  //   2. "…and a mutation was seen in the section" — the morph walks the whole
  //      section, so the first mutation lands somewhere else entirely (the
  //      drawer) long before the grid's images are touched.
  //
  // The signal that actually means "the new images are here" is the IMAGE SET
  // CHANGING. So the URLs are snapshotted up front and the hold is not released
  // until they differ AND everything near the viewport has decoded.
  //
  // ⚠️ A sort can legitimately return the same products (re-picking the sort you
  // are already on), in which case the set never changes — hence the second
  // release path, and the hard ceiling so the grid can never be left dimmed.
  function beginSwap() {
    var s = section();
    if (!s) return;
    var grid = s.querySelector('.coll_grid');
    if (!grid) return;

    function pendingNear(g) {
      var n = 0, imgs = g.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        var r = imgs[i].getBoundingClientRect();
        if (r.bottom > -200 && r.top < (window.innerHeight + 200) && !imgs[i].complete) n++;
      }
      return n;
    }

    grid.classList.add('is_swapping');
    clearInterval(swapTimer);

    // 🔴 RELEASE ON QUIET, NOT ON THE FIRST GOOD MOMENT.
    // There is more than one wave: the section morph lands first, and Horizon's
    // `paginated-list` then clears its cached pages and re-fetches on the same
    // filter event — so the grid was rebuilt AGAIN after the hold had already
    // lifted, which is where the surviving flash frames were coming from.
    // Any mutation therefore resets the quiet clock; the dim only lifts once the
    // grid has stopped changing AND nothing near the viewport is still loading.
    var started = Date.now();
    var lastMutation = started;
    var mo = new MutationObserver(function () { lastMutation = Date.now(); });
    mo.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });

    swapTimer = setInterval(function () {
      var live = section();
      var g2 = live && live.querySelector('.coll_grid');
      if (!g2) { clearInterval(swapTimer); mo.disconnect(); return; }

      var now = Date.now();
      var elapsed = now - started;
      var expired = elapsed > 3500;                 // ceiling — never stay dimmed
      var ready = pendingNear(g2) === 0;
      var quiet = (now - lastMutation) > 260;

      var done = expired || (ready && quiet && elapsed > 150);

      if (!done) return;

      clearInterval(swapTimer);
      mo.disconnect();
      // One more frame so the decoded images are painted before the dim lifts.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { g2.classList.remove('is_swapping'); });
      });
    }, 50);
  }

  // 🔴 A PERMANENT OBSERVER, NOT A TIMED POLL.
  //
  // This bug came back three times and every fix was a race: mark them revealed,
  // poll 2s, poll 4s. The morph can land later than any window you pick — on a
  // cold section render it does — and when it does, the re-rendered markup keeps
  // `data-reveal` with no `is_revealed`, so the section sits at `opacity: 0`.
  // That is why the TOOLBAR vanished too, not only the products: `.coll_toolbar`
  // carries `data-reveal` as well.
  //
  // There is no window any more. Anything added to this section at any point in
  // the page's life is revealed on the next frame.
  //
  // ⚠️ Mutation-introduced nodes are revealed INSTANTLY, no entrance animation,
  // deliberately: they are re-rendered products that were on screen a moment
  // ago. The FIRST paint still animates — there are no mutations at load, so the
  // IntersectionObserver handles it as before.
  // Reveal with NO entrance animation. Adding `.is_revealed` on its own still
  // fires the 0.7s transition and its stagger, which replays the entrance and
  // reads as the page reloading — so the transition is killed for one frame
  // while the class goes on, then handed straight back.
  // ⚠️ Handed back on a TIMER as well as rAF: rAF does not run in a tab the
  // browser is not painting, and without the fallback `pp_reveal_instant` could
  // stick, leaving `transition: none` across the whole grid.
  function revealInstantly(root) {
    var els = (root || document).querySelectorAll('[data-reveal]:not(.is_revealed)');
    if (!els.length) return 0;
    for (var i = 0; i < els.length; i++) {
      els[i].classList.add('pp_reveal_instant', 'is_revealed');
    }
    var handBack = function () {
      for (var j = 0; j < els.length; j++) els[j].classList.remove('pp_reveal_instant');
    };
    requestAnimationFrame(function () { requestAnimationFrame(handBack); });
    setTimeout(handBack, 120);
    return els.length;
  }

  function watchRevealsForever() {
    var host = document.querySelector('.coll-sec-2-wrap') || document.querySelector('.coll_sec_2');
    if (!host || host.__ppRevealWatched) return;
    host.__ppRevealWatched = true;

    // 🔴 `attributes` IS LOAD-BEARING. The morph does not replace these nodes —
    // it EDITS THEIR CLASS ATTRIBUTE, stripping `is_revealed` in place, so a
    // childList-only observer never fires and the section stays at opacity 0.
    //
    // 🔴 And the callback is SYNCHRONOUS, deliberately not debounced behind rAF.
    // It was, and that was its own bug on top: a `queued` flag was set, the rAF
    // that would clear it never ran in a non-painting tab, and every later
    // mutation early-returned forever.
    //
    // No debounce is needed — the pass only touches
    // `[data-reveal]:not(.is_revealed)`, so writing the class re-enters the
    // callback exactly once more, finds nothing, and stops.
    new MutationObserver(function () {
      revealInstantly(host);
    }).observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  function rescanReveals() {
    revealInstantly(document);   // immediate pass; the observer covers the rest
  }

  /* ---- 3. Load More ------------------------------------------------------ */
  var settle = null;
  var seen = -1;
  var gridObserver = null;

  function watchGrid() {
    var s = section();
    if (!s) return;
    var grid = s.querySelector('.coll_grid');
    if (!grid) return;

    if (gridObserver) gridObserver.disconnect();
    seen = grid.children.length;

    // The new cards are appended by Horizon, so a MutationObserver is the only
    // honest "it has landed" signal — there is no event to listen for.
    gridObserver = new MutationObserver(function () {
      if (grid.children.length === seen) return;
      var added = grid.children.length - seen;
      seen = grid.children.length;
      clearTimeout(settle);
      // 🔴 Register the new cards with the reveal observer, or they stay at
      // opacity 0 and Load More appears to produce a screenful of white space.
      if (typeof window.ppRevealScan === 'function') window.ppRevealScan(grid);
      finishLoad(added);
    });
    gridObserver.observe(grid, { childList: true });
  }

  function finishLoad(added) {
    var s = section();
    if (!s) return;
    var wrap = s.querySelector('[data-coll-more]');
    var grid = s.querySelector('.coll_grid');
    if (!wrap || !grid) return;

    wrap.classList.remove('is_loading');
    var btn = wrap.querySelector('[data-coll-more-btn]');
    if (btn) btn.disabled = false;

    var status = wrap.querySelector('[data-coll-status]');
    if (status && added > 0) status.textContent = added + ' more products loaded.';

    // Nothing left to fetch: the control retires rather than sitting there
    // doing nothing, which is what reads as broken. `data-last-page` is re-read
    // every time — a filter change rewrites it.
    var last = parseInt(grid.getAttribute('data-last-page') || '1', 10);
    var highest = 1;
    for (var i = 0; i < grid.children.length; i++) {
      var p = parseInt(grid.children[i].getAttribute('data-page') || '1', 10);
      if (p > highest) highest = p;
    }
    wrap.hidden = highest >= last;
    syncCount();
  }

  // "16 of 95 products": the first number is the cards actually in the grid.
  // Once the last page is in, the total is set to that too, so a Giftship
  // add-on the grid skips can never leave it reading "95 of 96".
  function syncCount() {
    var s = section();
    if (!s) return;
    var el = s.querySelector('[data-coll-count]');
    var grid = s.querySelector('.coll_grid');
    if (!el || !grid) return;
    var shown = grid.children.length;
    var last = parseInt(grid.getAttribute('data-last-page') || '1', 10);
    var highest = 1;
    for (var i = 0; i < grid.children.length; i++) {
      var p = parseInt(grid.children[i].getAttribute('data-page') || '1', 10);
      if (p > highest) highest = p;
    }
    var a = el.querySelector('[data-coll-shown]');
    var b = el.querySelector('[data-coll-total]');
    if (a && a.textContent !== String(shown)) a.textContent = shown;
    if (b && highest >= last && b.textContent !== String(shown)) b.textContent = shown;
  }

  /* ---- delegation -------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var colBtn = t.closest('.coll_cols_btn');
    if (colBtn) {
      var value = colBtn.getAttribute('data-cols');
      paintCols(value);
      try { sessionStorage.setItem(COLS_KEY, value); } catch (e2) {}
      return;
    }

    if (t.closest('[data-coll-filter-open]')) { reRendering = false; openDrawer(); return; }

    // 🔴 CLEAR ALL — unticks everything and re-renders in place.
    // The link's href is a real reset URL and is left working for no-JS, but
    // navigating would close the drawer and drop the chosen sort. Unticking the
    // form and asking the component to apply it keeps both: `sort_by` is a
    // checked RADIO inside the same form, so it survives untouched.
    var clearBtn = t.closest('[data-coll-clear]');
    if (clearBtn) {
      var cForm = document.querySelector('.coll_drawer facets-form-component');
      if (!cForm || typeof cForm.updateFilters !== 'function') return;   // let the link navigate
      e.preventDefault();
      var f = cForm.querySelector('form');
      if (f) {
        f.querySelectorAll('input[type="checkbox"]').forEach(function (i) { i.checked = false; });
        // Price is a pair of number inputs, not checkboxes.
        f.querySelectorAll('input[type="number"], input[name^="filter.v.price"]').forEach(function (i) { i.value = ''; });
      }
      // ⚠️ Only run the keep-the-drawer-open dance if it IS open. Reset now
      // lives in the toolbar as well, and from there `reopenAfterRender` would
      // helpfully OPEN a drawer the visitor never asked for.
      var wasOpen = !!(drawer() && drawer().open);
      beginSwap();
      rescanReveals();
      if (wasOpen) {
        reRendering = true;
        document.documentElement.classList.add('pp_facet_rerender');
        if (watching) cancelAnimationFrame(watching);
        reopenAfterRender(drawer(), 0);
      }
      cForm.updateFilters();
      return;
    }

    // A deliberate close must never be fought by the re-open above.
    if (t.closest('.coll_drawer_close, .coll_drawer_apply')) { reRendering = false; return; }

    var moreBtn = t.closest('[data-coll-more-btn]');
    if (moreBtn) {
      var wrap = moreBtn.closest('[data-coll-more]');
      if (!wrap || wrap.hidden || wrap.classList.contains('is_loading')) return;
      watchGrid();                       // re-point the observer at the live grid
      wrap.classList.add('is_loading');  // this is what reveals the sentinel
      moreBtn.disabled = true;

      // A network failure would otherwise leave the button spinning forever.
      // Dropping `is_loading` hides the sentinel again, so a retry is one click.
      clearTimeout(settle);
      settle = setTimeout(function () {
        wrap.classList.remove('is_loading');
        moreBtn.disabled = false;
      }, 10000);
      return;
    }
  });

  // A facet change inside the drawer is what starts a re-render — and is also
  // the only moment at which we still hold the OUTGOING dialog to compare against.
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('.coll_drawer')) return;

    // 🔴 The sort pills are radios named `sort_by` INSIDE the facets form, so
    // Horizon's own `createURLParameters(new FormData(facetsForm))` already
    // carries the sort — there is no hidden select to puppet. All this does is
    // ask the component to apply the form, and paint the new state on the click
    // frame rather than waiting for the round trip. That lag is the same one the
    // FAQ tabs and the About history slider both had to be fixed for.
    var sort = e.target.closest ? e.target.closest('[data-coll-sort]') : null;
    if (!sort && e.target.matches && e.target.matches('[data-coll-sort]')) sort = e.target;
    if (sort) {
      var grid = sort.closest('.coll_sort_grid');
      if (grid) {
        grid.querySelectorAll('.coll_sort_pill').forEach(function (p) {
          p.classList.toggle('is_active', p.contains(sort));
        });
      }
      var label = document.querySelector('[data-coll-sort-value]');
      var pill = sort.closest('.coll_sort_pill');
      if (label && pill) label.textContent = pill.textContent.trim();

      var host = sort.closest('facets-form-component');
      if (host && typeof host.updateFilters === 'function') host.updateFilters();
    }

    // 🔴 APPLY THE CURATED TAG CHECKBOXES OURSELVES.
    // They carry Horizon's own `name`/`value`/`ref` and sit inside
    // `facet-inputs-component` with `on:change="/updateFilters"`, exactly like
    // Horizon's own filter inputs — but that binding only fires if the element
    // has UPGRADED, and it had not: measured, `customElements.get(
    // 'facet-inputs-component')` was undefined on the page while
    // `facets-form-component` was defined. The tick was landing correctly (the
    // form's FormData gained `filter.p.tag=Baby Boy`) and nothing ever applied
    // it, so the URL never changed and every product stayed on screen.
    //
    // Calling the FORM component directly removes that dependency: it is the
    // thing that builds the URL and re-renders, and it is reliably upgraded.
    var curated = e.target.closest ? e.target.closest('.coll_facet_curated') : null;
    if (curated) {
      // 🔴 Mirror the visible checkbox onto its ALIASES before applying.
      // An aliased option ("Gender Neutral = baby unisex, Baby - Unisex")
      // renders one visible input plus a hidden one per extra tag. The label is
      // bound to the visible input only, so without this a tick would submit
      // just the first tag and silently return a fraction of the products —
      // 27 of 40, in the case that prompted this.
      var row = e.target.closest('.coll_facet_value');
      if (row) {
        var mates = row.querySelectorAll('input[type="checkbox"]');
        for (var m = 0; m < mates.length; m++) mates[m].checked = e.target.checked;
      }

      var form = e.target.closest('facets-form-component');
      if (form && typeof form.updateFilters === 'function') {
        form.updateFilters();
      } else {
        // Last resort: navigate. Better a full page load than a dead filter.
        console.warn('[pp] facets-form-component unavailable; falling back to navigation');
        var f = e.target.closest('form');
        if (f) {
          var params = new URLSearchParams(new FormData(f));
          var q = new URL(window.location.href).searchParams.get('q');
          if (q) params.set('q', q);
          window.location.search = params.toString();
        }
      }
    }

    reRendering = true;
    document.documentElement.classList.add('pp_facet_rerender');
    beginSwap();
    rescanReveals();
    if (watching) cancelAnimationFrame(watching);
    reopenAfterRender(drawer(), 0);
  }, true);

  document.addEventListener('close', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('coll_drawer')) {
      var btn = document.querySelector('[data-coll-filter-open]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }
  }, true);

  // 🔴 There is no CSS selector for "a sticky element has reached its pin", so
  // the bar is compared against its OWN resolved `top`. That way the test knows
  // nothing about the header and survives the scroll-shrink and every
  // breakpoint. Same technique as the FAQ page's tab bar.
  function trackPinned() {
    var bar = document.querySelector('[data-coll-toolbar-bar]');
    if (!bar) return;
    var top = parseFloat(getComputedStyle(bar).top) || 0;
    bar.classList.toggle('is_pinned', bar.getBoundingClientRect().top <= top + 0.5);
  }

  // 🔴 Above 990 the page scrolls `.page-wrapper`, not `window` — Horizon's
  // squeeze layout sets html/body to `overflow: hidden` there, so a `window`
  // scroll listener never fires. Both are bound; whichever is live wins.
  function bindScroll() {
    var pw = document.querySelector('.page-wrapper');
    if (pw) pw.addEventListener('scroll', trackPinned, { passive: true });
    window.addEventListener('scroll', trackPinned, { passive: true });
    window.addEventListener('resize', trackPinned, { passive: true });
  }

  function afterRender() {
    restoreCols();
    watchGrid();
    trackPinned();
    syncCount();
  }

  bindScroll();
  watchRevealsForever();
  document.addEventListener('DOMContentLoaded', watchRevealsForever);
  document.addEventListener('collection:update', function () { requestAnimationFrame(afterRender); });
  document.addEventListener('search:update',     function () { requestAnimationFrame(afterRender); });
  document.addEventListener('shopify:section:load', afterRender);
  document.addEventListener('DOMContentLoaded', afterRender);
  afterRender();
})();

/* ===========================================================================
   PRODUCT GALLERY LIGHTBOX — Fancybox 5, product template only (2026-09-03)
   ===========================================================================
   🔴 IT LIVES HERE, NOT IN A SECTION. It shipped first inside
   `sections/product-sec-1.liquid`, and that made a piece of BEHAVIOUR depend on
   a merchant remembering to place a section — the lightbox silently did nothing
   until then, which is exactly how it was reported. Markup needs a section;
   behaviour does not.

   🔴 IT BINDS BY WALKING THE RENDERED IMAGES, NOT BY A CLASS CONTRACT. The
   gallery is `_product-media-gallery`, a theme block in `blocks/` that Theme Kit
   cannot sync, so its class names are Horizon's to change on any theme update.
   Reading `img` elements out of `.product-information__media` survives that.

   🔴 THE CLICK IS TAKEN IN THE CAPTURE PHASE AND STOPPED. Horizon ships its own
   `<zoom-dialog>` on the gallery; without this both open and the visitor gets
   our lightbox behind their dialog.

   ⚠️ Fancybox is fetched ON DEMAND — only on a product page that actually has a
   gallery, and only once. It is ~70KB, and this build's GEO brief counts
   payload as an SEO cost. The press gallery loads the same CDN file, so a
   visitor who has seen one gets the other from cache.
   =========================================================================== */
(function () {
  var MEDIA = '.product-information__media';
  var CSS   = 'https://cdn.jsdelivr.net/npm/@fancyapps/ui@5.0.36/dist/fancybox/fancybox.css';
  var JS    = 'https://cdn.jsdelivr.net/npm/@fancyapps/ui@5.0.36/dist/fancybox/fancybox.umd.js';
  var asked = false;
  var pending = null;

  function load() {
    if (asked) return;
    asked = true;
    var l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = CSS;
    document.head.appendChild(l);
    var s = document.createElement('script');
    s.src = JS;
    s.onload = function () {
      /* If the visitor clicked before the script landed, honour that click
         rather than swallowing it — the first click is the one that matters. */
      if (pending) { show(pending.root, pending.index); pending = null; }
    };
    document.head.appendChild(s);
  }

  function srcAt(img, w) {
    /* Shopify's CDN takes a `width` parameter — swap it rather than building a
       URL, so any other transform already on it survives. */
    var s = img.currentSrc || img.src || '';
    if (!s) return '';
    return /[?&]width=/.test(s) ? s.replace(/([?&]width=)\d+/, '$1' + w)
                                : s + (s.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* The image's identity, independent of the size it happens to be rendered at
     — the Shopify CDN puts the width in the query string, so two <img> for the
     same photo differ only there. */
  function key(im) {
    return (im.currentSrc || im.src || '').split('?')[0];
  }

  /* 🔴 EVERY PHOTO, NOT ONLY THE MEASURABLE ONES — and DE-DUPLICATED.
     Two earlier versions of this got it wrong in opposite directions:

     1. Filtering on `offsetParent !== null` excluded EVERY gallery image
        (measured `false` on a picture plainly on screen), so the handler bailed
        and nothing ever opened. `offsetParent` is null inside a fixed-positioned
        ancestor, which Horizon's gallery chain has; it tests "is it in the normal
        flow", not "is it visible".
     2. Filtering on a non-zero RECT then opened correctly but with ONE slide at
        desktop — Horizon renders the gallery twice (a slideshow and a grid, one
        per breakpoint) and sizes only the visible copy, so every other photo
        measured 0x0 and was dropped from the gallery.

     So: take intrinsic `naturalWidth`, which is the real photo whether or not
     that copy is laid out, and de-duplicate by URL so the two renderings
     contribute one slide each rather than two. */
  function items(root) {
    var seen = {};
    var out = [];
    [].slice.call(root.querySelectorAll('img')).forEach(function (im) {
      var k = key(im);
      if (!k || seen[k]) return;
      if ((im.naturalWidth || 0) <= 120) return;   /* a badge, an icon, a sprite */
      seen[k] = true;
      out.push(im);
    });
    return out;
  }

  function indexOfImage(root, img) {
    var k = key(img);
    var list = items(root);
    for (var i = 0; i < list.length; i++) { if (key(list[i]) === k) return i; }
    return -1;
  }

  function show(root, index) {
    if (!window.Fancybox) return false;
    var list = items(root).map(function (im) {
      return { src: srcAt(im, 2000), thumb: srcAt(im, 200), type: 'image', caption: im.alt || '' };
    });
    if (!list.length) return false;
    window.Fancybox.show(list, { startIndex: Math.max(0, index) });
    return true;
  }

  /* 🔴 NEUTER HORIZON'S OWN ZOOM RATHER THAN RACE IT.
     Intercepting the click on `window` in the capture phase was not enough, and
     the reason is worth recording: their `<zoom-dialog>` is NOT opened from a
     `click` listener, so `stopImmediatePropagation` on click never reaches it.
     Both overlays appeared — their full-bleed viewer on top, our lightbox
     behind — and closing theirs revealed ours, which is exactly how it was
     reported.
     ⚠️ IT DID NOT REPRODUCE FROM THE AUTOMATED BROWSER, and that is the lesson:
     a synthetic `el.click()` dispatches ONLY a click event, so a handler bound
     to `pointerdown` / `pointerup` never runs and the page looks correct. A
     real pointer emits the whole sequence. **When a user reports an interaction
     bug that a scripted click cannot reproduce, suspect a different event, not
     the user.**
     So the dialog's own open methods are replaced with no-ops. Nothing is
     removed from the DOM — deleting the element would make whatever holds a
     reference to it throw — and no event needs to be intercepted at all. */
  function neuter() {
    var hosts = document.querySelectorAll('zoom-dialog');
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      if (host.__ppNeutered) continue;
      host.__ppNeutered = true;
      if (typeof host.showDialog === 'function') host.showDialog = function () {};
      if (typeof host.open === 'function') host.open = function () {};
      var d = host.querySelector('dialog');
      if (d) {
        d.showModal = function () {};
        d.show = function () {};
        if (d.open && d.close) d.close();
      }
    }
  }

  if (!document.body || document.body.className.indexOf('temp_product') === -1) return;

  neuter();
  document.addEventListener('DOMContentLoaded', neuter);
  document.addEventListener('shopify:section:load', neuter);
  /* The gallery is re-rendered on a variant change, so the dialog can be a new
     element; re-neuter whenever one appears rather than holding a reference. */
  if (window.MutationObserver) {
    new MutationObserver(neuter).observe(document.body, { childList: true, subtree: true });
  }

  /* Warm it as soon as the pointer reaches the gallery, so the first click is
     instant; the click handler still covers a visitor who never hovers. */
  document.addEventListener('pointerenter', function (e) {
    if (e.target.closest && e.target.closest(MEDIA)) load();
  }, true);

  /* 🔴 ON `window`, IN CAPTURE, WITH `stopImmediatePropagation`. Horizon opens
     its own `<zoom-dialog>` from a DELEGATED CAPTURE listener on `document`,
     registered when its ES module runs — which is in the head, i.e. before this
     deferred file. Capture order is registration order per node, so a second
     `document` capture listener runs AFTER theirs and cannot preempt it: the
     zoom dialog opened first and our lightbox appeared behind it.
     `window` is one node further out in the capture path, so a listener here
     runs before ANY document-level listener whenever it was registered.
     ⚠️ `stopPropagation` alone is not enough even here — it stops the event
     reaching later NODES but not other listeners on this same one.
     ⚠️ Only `click` is intercepted, never `pointerdown`: the mobile gallery is a
     real swipe surface and swallowing pointer events there would break it. */
  window.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var root = e.target.closest(MEDIA);
    if (!root) return;
    /* 🔴 THE CLICK DOES NOT LAND ON THE `<img>`. Horizon lays a transparent
       `button.product-media-container__zoom-button` OVER each photo, so
       `closest('img')` is null and an earlier version bailed here — which is
       why, once their own zoom was neutered, clicking the picture did nothing
       at all. Measured with `elementsFromPoint` at the image's centre:
         button.button.button-unstyled  <- the click target
         img.product-media__image       <- the photo, underneath it
       So resolve the photo from the CELL the click happened in. Coordinates are
       tried only as a fallback, because a keyboard activation has none. */
    var img = e.target.tagName === 'IMG' ? e.target : e.target.closest('img');

    if (!img) {
      var cell = e.target.closest('.product-media-container, .product-media, li');
      if (cell) img = cell.querySelector('img');
    }
    if (!img && e.clientX) {
      var stack = document.elementsFromPoint(e.clientX, e.clientY) || [];
      for (var i = 0; i < stack.length; i++) {
        if (stack[i].tagName === 'IMG' && root.contains(stack[i])) { img = stack[i]; break; }
      }
    }
    if (!img) return;                       /* an arrow, a thumbnail rail — leave it */

    var idx = indexOfImage(root, img);
    if (idx < 0) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    neuter();

    if (!show(root, idx)) { pending = { root: root, index: idx }; load(); }
  }, true);
})();

/* ===========================================================================
   PRODUCT — Judge.me's STORE SUMMARY restyled as the design's "Summary of
   Reviews" band, with a Show More (2026-09-03)
   ===========================================================================
   🔴 NO SECTION, AND NOTHING TO ADD IN THE EDITOR. The summary is an app block
   already placed inside `product-information`, so wrapping it in a section of
   ours would mean the merchant MOVING it — and this build has now been bitten
   three times by a piece of the design depending on a section being placed.
   The block is styled where it sits and the two missing pieces (the heading and
   the Show More) are injected here.

   🔴 THE DESIGN IS A DIFFERENT COMPOSITION, NOT A RESKIN. Judge.me renders:
     heading + rating -> summary text -> keyword pills -> AI disclaimer -> button
   The design (460:4536-4539) draws:
     sparkles + "Summary of Reviews" -> AI disclaimer -> summary text + Show More
   so the disclaimer is REORDERED above the text (flex `order`, in the SCSS) and
   the rating row, the pills and their button are hidden. Their rating is not
   lost — it is on the review widget directly below, which is where the design
   puts it too.
   =========================================================================== */
(function () {
  if (!document.body || document.body.className.indexOf('temp_product') === -1) return;

  var MORE = 'Show More';
  var LESS = 'Show Less';

  /* 🔴 THE LABEL COMES FROM A `<template>` IN THE LAYOUT, not from a string
     here. It carries `icon-sparkle.svg` — the SAME artwork home section 6 uses
     — through `inline_asset_content`, so there is exactly one copy of those
     paths in the theme. An SVG hand-copied into this file would drift silently
     the first time the icon is retouched. */
  /* Returns the label AND the disclaimer, in order — the template holds both so
     the wording and the icon live in Liquid rather than in this file. */
  function heading() {
    var tpl = document.getElementById('pp-ai-head');
    if (tpl && tpl.content) return [].slice.call(tpl.content.cloneNode(true).children);
    var el = document.createElement('div');       /* template missing — still label it */
    el.className = 'pp_ai_head';
    el.innerHTML = '<span class="pp_ai_title">Summary of Reviews</span>';
    return [el];
  }

  function build(root) {
    if (!root || root.__ppDone) return;
    var stack = root.querySelector('.jm-stack');
    var text  = root.querySelector('.jm-store-summary-text');
    if (!stack || !text) return;              /* not rendered yet */
    root.__ppDone = true;

    /* ---- the heading the design has and the widget does not --------------- */
    var head = heading();
    for (var h = head.length - 1; h >= 0; h--) stack.insertBefore(head[h], stack.firstChild);

    /* ---- Show More --------------------------------------------------------
       🔴 The clamp is a CLASS, not an inline style, so the CSS owns the number
       of lines and it can differ per breakpoint. The button is only revealed
       when the text actually overflows — a "Show More" on a two-line summary is
       a control that does nothing — and that is re-checked on resize, because
       the same copy overflows at 430 and does not at 1600. */
    text.classList.add('pp_ai_text', 'is_clamped');

    /* 🔴 TWO DIFFERENT CONTROLS BEHIND ONE LABEL, chosen by whether a URL is
       configured in Theme settings -> AI Search & Business Info.

         url set   -> an <a> to the all-reviews page. It is ALWAYS shown, because
                      it is now navigation rather than disclosure: "there is more
                      to read, over here" is true whether or not this particular
                      summary happens to overflow three lines.
         url blank -> the original expand/collapse <button>, revealed only when
                      the text actually overflows.

       📌 The label and the URL come from the `<template>`'s data attributes, not
       from strings here — same reasoning as the sparkle: the wording stays in
       Liquid where it can become a setting without touching this file. */
    /* 🔴 TWO SOURCES, SECTION FIRST. `sections/product-reviews.liquid` renders
       `#pp-ai-more-cfg` with its own Button Text / Button Link fields, which is
       where a merchant will look for them — right beside the widgets. The
       `<template>` the layout renders carries the THEME-SETTING values and is
       the fallback, so the band still works with no section placed. */
    var cfgEl    = document.getElementById('pp-ai-more-cfg');
    var tplEl    = document.getElementById('pp-ai-head');
    var srcEl    = cfgEl || tplEl;
    var moreUrl  = srcEl ? (srcEl.getAttribute('data-more-url') || '').trim() : '';
    var moreText = (srcEl && (srcEl.getAttribute('data-more-label') || '').trim()) || MORE;

    var btn;
    if (moreUrl) {
      btn = document.createElement('a');
      btn.className = 'pp_ai_more';
      btn.href = moreUrl;
      btn.textContent = moreText;
      text.parentNode.insertBefore(btn, text.nextSibling);
      return;                       /* no clamp toggle, and nothing to measure */
    }

    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pp_ai_more';
    btn.textContent = moreText;
    btn.setAttribute('aria-expanded', 'false');
    btn.hidden = true;
    text.parentNode.insertBefore(btn, text.nextSibling);

    btn.addEventListener('click', function () {
      var open = text.classList.toggle('is_clamped') === false;
      btn.textContent = open ? LESS : MORE;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    function check() {
      /* Measure against the CLAMPED state, or an expanded block always reports
         no overflow and the control hides itself the moment it is used. */
      var wasOpen = !text.classList.contains('is_clamped');
      if (wasOpen) text.classList.add('is_clamped');
      var overflows = text.scrollHeight - text.clientHeight > 2;
      if (wasOpen) text.classList.remove('is_clamped');
      btn.hidden = !overflows && !wasOpen;
    }

    check();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
    window.addEventListener('resize', check, { passive: true });
  }

  function scan() {
    var w = document.querySelector('.jdgm-store-summary-widget');
    if (w) build(w);
  }

  /* Their widget renders asynchronously, so watch for it rather than polling —
     the same shape the review carousel needed. */
  scan();
  document.addEventListener('DOMContentLoaded', scan);
  if (window.MutationObserver) {
    var mo = new MutationObserver(function () { scan(); });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();


/* ===========================================================================
   PRODUCT REVIEWS — the design's ORDER, and the fields Judge.me already ships
   ===========================================================================
   Design 460:4406. Their widget and the design are the same four pieces in a
   different order:

     Judge.me   "Customer Reviews" -> score -> photos -> sort -> list
     design      score (centred) -> AI SUMMARY -> media -> list

   🔴 ONE DOM MOVE, AND ONLY ONE. The AI summary is a SEPARATE app block, so no
   amount of flex `order` can interleave it into the review widget's children —
   `order` reorders siblings, and these are not siblings. The block is moved into
   `.jdgm-rev-widg__header` in front of `.jdgm-row-media`; every other difference
   is paint, in `_productPage.scss`.
   📌 The alternative — a chain of `display: contents` down five of Judge.me's
   wrappers to flatten their tree into the section — was rejected: it discards
   each wrapper's box (their widths and padding go with it) and it would break
   silently the first time they set `display` on any of those elements.

   🔴 THREE APP BLOCKS ON THIS PAGE SHARE THE CLASS `jdgm-horizon-widget` — the
   preview badge beside the price, the AI summary, and the review widget. A probe
   that keyed on that class alone picked the badge. Always discriminate on what
   the block CONTAINS.

   🔴 EVERYTHING ELSE HERE IS TEXT THAT WAS ALREADY IN THE MARKUP. The date ships
   hidden (`<time datetime>` + `display: none`), the score is inside their link's
   "4.47 out of 5", the count is inside "Based on 17 reviews". Nothing is fetched
   and nothing is invented — the same lesson home section 6 taught, where the
   product link and the date both turned out to be there already.
   =========================================================================== */
(function () {
  if (!document.body || document.body.className.indexOf('temp_product') === -1) return;

  var MEDIA_TITLE = 'Reviews with Media';

  /* 🔴 IDEMPOTENT BY CONTENT, NOT BY A FLAG ON THE ELEMENT — and that distinction
     is the whole bug this cost. A `el.__pp = true` guard marks the ELEMENT as
     done, but Judge.me finishes its own setup AFTER our first pass and rewrites
     `textContent` on the very same node. The element survives, so the flag
     survives with it, and the repair is then blocked for ever: measured
     `cntMarked: true` with the text still reading "Based on 17 reviews".
     Comparing against the TARGET STRING instead is self-healing — it re-fixes
     whatever they overwrite — and it still cannot loop, because once the text
     equals the target the next pass writes nothing and emits no mutation. */
  function setText(el, want) {
    if (!el || want == null || el.textContent === want) return;
    el.textContent = want;
  }

  /* The score, as the design draws it: the number alone, not "4.47 out of 5".
     ⚠️ It stays an <a> to Judge.me's profile — that link is their attribution. */
  function score(root) {
    var a = root.querySelector('.jdgm-rev-widg__summary-average a');
    if (!a) return;
    var m = (a.textContent || '').match(/[\d.]+/);
    if (m) setText(a, m[0]);
  }

  /* "Based on 17 reviews" -> "17 Reviews", the design's wording. */
  function count(root) {
    var el = root.querySelector('.jdgm-rev-widg__summary-text');
    if (!el) return;
    var m = (el.textContent || '').match(/\d[\d,]*/);
    if (m) setText(el, m[0] + ' Review' + (m[0] === '1' ? '' : 's'));
  }

  function mediaTitle(root) {
    setText(root.querySelector('.jdgm-gallery-title'), MEDIA_TITLE);
  }

  /* 🔴 THE DATE NEEDED NO INJECTION — only revealing and reformatting. Their
     `<time>` carries a real `datetime`, so the format is ours to choose.
     📌 Formatted through `toLocaleDateString` with the STOREFRONT's own lang
     rather than a hardcoded DD/MM vs MM/DD, which is a guess that is wrong in
     half the world. */
  function dates(root) {
    var lang = document.documentElement.getAttribute('lang') || 'en-US';
    var list = root.querySelectorAll('.jdgm-rev__timestamp');
    for (var i = 0; i < list.length; i++) {
      var t = list[i];
      var raw = t.getAttribute('datetime');
      if (!raw) continue;
      var d = new Date(raw);
      if (isNaN(d.getTime())) continue;
      /* The target is derived from the `datetime` ATTRIBUTE, never from the
         text we may already have written — so this stays idempotent however
         many times it runs. */
      var want;
      try {
        want = d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: '2-digit' });
      } catch (e) {
        want = d.toLocaleDateString();
      }
      setText(t, want);
    }
  }

  /* ---- the media track's arrow -------------------------------------------
     🔴 ONLY WHEN THE THUMBS ACTUALLY OVERFLOW. The track is a native scroll-snap
     container (CSS), so a swipe already works with no JS; the arrow is the
     desktop affordance on top of it. A control that cannot move anything is
     worse than no control, so it is removed again if the row stops overflowing.
     ⚠️ Verify with `scrollWidth > clientWidth`, never by counting thumbs — the
     number that fits changes with the viewport. */
  function arrow(root) {
    var gal = root.querySelector('.jdgm-gallery');
    var row = root.querySelector('.jdgm-row-media');
    if (!gal || !row) return;

    var over = gal.scrollWidth - gal.clientWidth > 2;
    var btn  = row.querySelector('.pp_gallery_arrow');

    if (!over) { if (btn) btn.remove(); return; }
    if (btn) return;

    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pp_gallery_arrow';
    btn.setAttribute('aria-label', 'Next');
    row.appendChild(btn);

    btn.addEventListener('click', function () {
      var first = gal.querySelector('.jdgm-gallery__thumbnail-link');
      if (!first) return;
      /* one thumb plus the gap — read, never assumed, so the CSS ladder owns it */
      var step = first.getBoundingClientRect().width + (parseFloat(getComputedStyle(gal).columnGap) || 0);
      var atEnd = gal.scrollLeft + gal.clientWidth >= gal.scrollWidth - 2;
      gal.scrollTo({ left: atEnd ? 0 : gal.scrollLeft + step, behavior: 'smooth' });
    });
  }

  /* ---- the one move ------------------------------------------------------- */
  function place() {
    var summary = document.querySelector('.jdgm-store-summary-widget');
    if (!summary) return false;
    var block = summary.closest('.shopify-block');
    var head  = document.querySelector('.jdgm-rev-widg__header');
    if (!block || !head) return false;

    /* 🔴 THE MEDIA ROW IS OPTIONAL, AND REQUIRING IT WAS A BUG.
       `.jdgm-row-media` exists only when a product HAS review photos. Gating the
       move on it meant that on every product without any, `place()` bailed and
       the summary stayed OUTSIDE the widget — which showed up as two faults at
       once: it rendered ABOVE the rating instead of below it, and, as a direct
       child of the section, it ran the full viewport width (measured 1600 at
       left 0 on `gardeners-nook`, against 1219.7 at 190.2 on a product that does
       have photos).
       📌 Both symptoms, one cause. When two unrelated-looking things break
       together on SOME pages only, look for the single guard that is failing. */
    var media = head.querySelector('.jdgm-row-media');

    /* Idempotent, and that is what stops the MutationObserver below looping:
       a move fires mutations, so the second pass MUST be a no-op. */
    if (media) {
      if (media.previousElementSibling === block) return true;
      head.insertBefore(block, media);
      return true;
    }

    /* No photos on this product — append instead, which puts the summary after
       the rating. Same position in the design's order; there is simply nothing
       following it. */
    if (head.lastElementChild === block) return true;
    head.appendChild(block);
    return true;
  }

  /* 🔴 A PRODUCT WITH NO REVIEWS LEAVES AN EMPTY BAND. Judge.me hides its own
     widget entirely (`display: none`, and its `.jdgm-temp-hiding-style` never
     lifts) when a product has none — which is right — but the SECTION's own
     "Space Above" is still painted, so the page carried a measured 90px of
     blank with zero visible descendants between the product details and the
     shipping band.
     ⚠️ Gated on their `jdgm--done-setup-widget` class, NOT on height alone:
     before their setup runs the widget is legitimately 0-tall, and collapsing
     on that would flip the section closed and open again on every load.
     📌 The class is only written when it actually CHANGES. `classList.toggle`
     with a matching force value happens to emit no mutation record, but the
     note in this file's carousel section is explicit that relying on that is
     luck — and this runs inside a MutationObserver. */
  function collapse() {
    var sec = document.querySelector('.pp_reviews_sec');
    if (!sec) return;
    var widg = sec.querySelector('.jdgm-widget.jdgm-review-widget');
    if (!widg || widg.className.indexOf('jdgm--done-setup-widget') === -1) return;

    var empty = widg.getBoundingClientRect().height === 0;
    if (sec.classList.contains('is_empty') !== empty) {
      sec.classList.toggle('is_empty', empty);
    }
  }

  function run() {
    collapse();
    var widg = document.querySelector('.jdgm-rev-widg');
    if (!widg) return;
    place();
    score(widg);
    count(widg);
    mediaTitle(widg);
    dates(widg);          /* re-runs after Load More adds rows */
    arrow(widg);
  }

  run();
  document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('load', run);
  window.addEventListener('resize', function () {
    var widg = document.querySelector('.jdgm-rev-widg');
    if (widg) arrow(widg);
  }, { passive: true });

  /* Their widget renders asynchronously and re-renders on sort and on Load More,
     so watch rather than poll. Every function above is guarded, so a callback
     that finds nothing to do costs one query. */
  if (window.MutationObserver) {
    new MutationObserver(function () { run(); })
      .observe(document.body, { childList: true, subtree: true });
  }
})();


/* ===========================================================================
   YOU MAY ALSO LOVE — fetch the recommendations, render OUR card
   ===========================================================================
   🔴 SHOPIFY ONLY POPULATES `recommendations.products` ON ITS OWN ENDPOINT.
   On the initial product render `recommendations.performed` is false, so
   `sections/product-recs.liquid` renders empty; requesting
   `routes.product_recommendations_url` with this section's `section_id` renders
   the SAME section again, that time with the products, and the inner HTML is
   swapped in. This is Shopify's documented mechanism — Horizon's own section
   does exactly the same thing — not a workaround.

   📌 Fetched from an IntersectionObserver with a wide margin rather than on
   load: the band sits at the foot of a long page and the request costs a
   round-trip plus four product cards of markup. Same reasoning as the map and
   the YouTube facade.
   =========================================================================== */
(function () {
  var sec = document.querySelector('[data-pp-recs]');
  if (!sec) return;

  var url = sec.getAttribute('data-url');
  var slot = sec.querySelector('.pp_recs_inner');
  if (!url || !slot) return;

  var done = false;

  function load() {
    if (done) return;
    done = true;

    fetch(url)
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('.pp_recs_inner');
        /* No recommendations for this product — leave the section empty. The
           `:has(.pp_recs_grid)` rule then keeps its margin collapsed too, so an
           empty band cannot print a gap above nothing. */
        if (!fresh || !fresh.querySelector('.pp_recs_grid')) return;

        slot.innerHTML = fresh.innerHTML;

        /* 🔴 The cards arrive AFTER the store-wide reveal observer has finished
           its pass, so without this they would sit at `opacity: 0` for ever —
           the same hook the Judge.me carousel needed. */
        if (typeof window.ppRevealScan === 'function') window.ppRevealScan(slot);
      })
      .catch(function (e) {
        /* 🔴 Logged, never swallowed. A silent catch here would leave an empty
           band and a clean console — precisely the failure mode that cost four
           deploys on the map. */
        if (window.console) console.warn('[pp] recommendations failed:', e);
      });
  }

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); load(); return; }
      }
    }, { rootMargin: '600px 0px' });
    io.observe(sec);
  } else {
    load();
  }
})();


/* ===========================================================================
   GIFTSHIP — the design's accordion, built around the app's own markup
   ===========================================================================
   Design 460:4406: a black "Looking to Personalize Your Gift?" bar, then the
   Message Greeting expanded, then EVERY other option as a collapsed grey row
   with a chevron.

   🔴 GIFTSHIP RENDERS NONE OF THAT. Its `<select>` options already read as grey
   rows once styled, but the Specialty Item is an inline CAROUSEL and the text
   options are open fields — so the rows have to be built here, around markup we
   cannot author. `<details>`/`<summary>` is used rather than a hand-rolled
   toggle: correct keyboard behaviour for free, and the content stays in the DOM
   whatever its open state.

   🔴 A `<select>` IS LEFT ALONE. It already looks exactly like a collapsed row
   and clicking it opens the native option list — wrapping one in a second
   collapsible would mean two chevrons and two clicks to reach one value.

   ⚠️ The FIRST option stays expanded, per the design: the gift message is the
   one field almost every buyer fills, so hiding it behind a row would put a
   click in front of the common case.
   =========================================================================== */
(function () {
  /* 🔴 ALSO ON THE MULTI-ADDRESS PAGE. Giftship's "Edit" modal renders the same
     option set, so it gets the same accordion — otherwise the product page has
     collapsible grey rows and the modal has a flat list of the same fields. */
  if (!document.body) return;
  var cls = document.body.className;
  if (cls.indexOf('temp_product') === -1 && cls.indexOf('pg_gs_proxy') === -1) return;

  var WRAPPED = 'pp_gs_wrapped';

  /* 🔴 THE QUESTION IS NOT ALWAYS IN A `<label>`. Giftship's upsell option — the
     Specialty Item carousel, and the only option the design needs collapsing —
     carries its question in `.gs__upsell-label` and leaves the `<label>` EMPTY.
     Keying on `<label>` alone found nothing and silently wrapped no rows at all.
     📌 The tell was `rowCount: 0` with the group bar built correctly: the
     wrapper ran, so it was the label lookup that failed, not the pass. */
  function labelFor(opt) {
    var el = opt.querySelector('label');
    var t = el ? (el.textContent || '').trim() : '';
    if (!t) {
      el = opt.querySelector('.gs__upsell-label');
      t = el ? (el.textContent || '').trim() : '';
    }
    return t.replace(/\s+/g, ' ');
  }

  /* One option -> one collapsed grey row. */
  function wrapOption(opt, index) {
    if (opt.classList.contains(WRAPPED)) return;
    /* Never touch a conditional option: Giftship shows and hides these itself
       (`.gs__hidden-option`), and a wrapper of ours would fight that. */
    if (opt.classList.contains('gs__hidden-option')) return;
    if (opt.querySelector('select')) return;      /* already a row — see above */
    /* A DATE stays open. The pre-order ship date is required, and a required
       field hidden inside a closed row only surfaces as Giftship's "please
       complete the required fields" error on Add to Cart (2026-10-01). */
    if (opt.querySelector('.gs__delivery-date, .flatpickr-input')) return;
    if (index === 0) return;                      /* the message stays open */

    var text = labelFor(opt);
    if (!text) return;

    opt.classList.add(WRAPPED);

    var det = document.createElement('details');
    det.className = 'pp_gs_row';
    var sum = document.createElement('summary');
    sum.className = 'pp_gs_row_head';
    sum.textContent = text;
    det.appendChild(sum);

    opt.parentNode.insertBefore(det, opt);
    det.appendChild(opt);

    /* The label is now the summary; showing it again inside the panel repeats
       the question, which is exactly what the design does not do. Both carriers
       are hidden, since either may be the one holding it. */
    var l = opt.querySelector('label');
    if (l) l.style.display = 'none';
    var u = opt.querySelector('.gs__upsell-label');
    if (u) u.style.display = 'none';
  }

  /* The black bar, wrapping the whole set. */
  function wrapGroup(wrap) {
    if (wrap.parentNode && wrap.parentNode.classList.contains('pp_gs_group')) return;
    var cfg = document.getElementById('pp-gs-cfg');
    var title = cfg ? (cfg.getAttribute('data-group-title') || '').trim() : '';
    if (!title) return;                            /* blank hides the bar */

    var det = document.createElement('details');
    det.className = 'pp_gs_group';
    det.open = true;                               /* the design draws it expanded */
    var sum = document.createElement('summary');
    sum.className = 'pp_gs_group_head';
    sum.textContent = title;
    det.appendChild(sum);

    wrap.parentNode.insertBefore(det, wrap);
    det.appendChild(wrap);
  }

  /* 🔴 EVERY OPTION IN A SELECT REPEATS THE WHOLE QUESTION.
     Giftship names each choice "<question> - <choice> (+$6.00)", so opening the
     Greeting Card list showed "Would you like to add a Greeting Card?" SEVENTEEN
     times, each with a different tail. The native option list cannot be styled
     at all, so the only lever is the text itself — and the question is already
     on the closed row, which is where the design puts it.
     ⚠️ ONLY the label is rewritten. `option.value` is untouched, so what
     Giftship reads and what reaches the cart are exactly as before.
     📌 The prefix is taken from the placeholder (the first option), not from a
     pattern — their wording is theirs to change. */
  function trimOptions(sel) {
    if (sel.__ppTrimmed || sel.options.length < 2) return;
    var prefix = (sel.options[0].textContent || '').trim();
    if (!prefix) return;
    sel.__ppTrimmed = true;

    for (var i = 1; i < sel.options.length; i++) {
      var o = sel.options[i];
      var t = (o.textContent || '').trim();
      if (t.indexOf(prefix) !== 0) continue;
      var rest = t.slice(prefix.length).replace(/^\s*[-–—:]\s*/, '').trim();
      if (rest) o.textContent = rest;
    }
  }

  function build() {
    /* ⚠️ ALL of them, not `#gsAppContainer`'s one. On the product page the
       container carries that id; inside the Edit modal it is
       `.gs__app-container.gs__product-app-container` with NO id at all, so an
       id-based query found nothing there and the modal kept a flat list. */
    var wraps = document.querySelectorAll('.gs__form-options-wrapper');

    for (var w = 0; w < wraps.length; w++) {
      var wrap = wraps[w];

      var opts = wrap.querySelectorAll('.gs__product-option');
      for (var i = 0; i < opts.length; i++) wrapOption(opts[i], i);

      var sels = wrap.querySelectorAll('select.gs__product-element');
      for (var j = 0; j < sels.length; j++) trimOptions(sels[j]);

      wrapGroup(wrap);
    }
  }

  build();
  document.addEventListener('DOMContentLoaded', build);
  window.addEventListener('load', build);

  /* Their options render asynchronously and re-render when a conditional option
     is revealed, so watch. Everything above is idempotent — a pass with nothing
     to do costs one query and writes nothing, so this cannot loop. */
  if (window.MutationObserver) {
    new MutationObserver(function () { build(); })
      .observe(document.body, { childList: true, subtree: true });
  }
})();


/* ===========================================================================
   PRODUCT TABS — Description / Details / Delivery / Brands
   ===========================================================================
   The strip's LOOK and its overflow affordance come from the shared
   `[data-pp-tab-bar]` handler further up this file; this only swaps the panels.

   🔴 DELEGATED FROM THE CONTAINER, not bound per pill. The section can be
   re-rendered by the theme editor at any time, and a handler holding node
   references would be silently lost — the lesson the collection page's filter
   morph taught.
   =========================================================================== */
(function () {
  if (!document.body || document.body.className.indexOf('temp_product') === -1) return;

  document.addEventListener('click', function (e) {
    var tab = e.target.closest ? e.target.closest('[data-pp-ptab]') : null;
    if (!tab) return;

    var root = tab.closest('[data-pp-ptabs]');
    if (!root) return;

    var key = tab.getAttribute('data-pp-ptab');

    var tabs = root.querySelectorAll('[data-pp-ptab]');
    for (var i = 0; i < tabs.length; i++) {
      var on = tabs[i] === tab;
      tabs[i].classList.toggle('is_active', on);
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
    }

    var panels = root.querySelectorAll('[data-pp-ptab-panel]');
    for (var j = 0; j < panels.length; j++) {
      panels[j].classList.toggle(
        'is_active',
        panels[j].getAttribute('data-pp-ptab-panel') === key
      );
    }
  });
})();


/* ===========================================================================
   PRODUCT TABS — placement, the description hand-off, and "See More"
   ---------------------------------------------------------------------------
   🔴 THE DESIGN PUTS THE STRIP INSIDE THE PRODUCT DETAILS COLUMN, under the
   description and directly above the Giftship options — and a Shopify SECTION
   can only render BELOW `product-information`, full width. `product-information`
   is composed of theme blocks in `blocks/`, which Theme Kit cannot sync, so
   there is no block of ours to put there. One DOM move solves it, exactly as it
   does for the Judge.me AI summary further up this file.

   Measured insertion point (2026-09-08, at 1600):
     .product-details > .group-block > .group-block-content   flex column, row-gap 14px
       [0] .view-product-title
       [1] product-sku-component
       [2] .group-block            (price / rating / quantity)
       [3] rte-formatter           <- the description
       [4] .buy-buttons-block      <- Giftship injects #gsAppContainer in here
   So the strip goes between [3] and [4], and the parent's own 14px row-gap
   supplies the spacing — the section's own `margin-top` is dropped once moved.

   🔴 THE SECTION STAYS THE SOURCE OF THE MARKUP, deliberately. Its Details /
   Delivery / Brands copy is content the merchant edits, and this build has
   already learned that a setting the merchant cannot find is, to them, a
   hardcoded value (the reviews "Show More"). So the settings stay on a section
   in the product template and only the RENDERED markup is relocated.
   ⚠️ The cost is that the section still has to be placed once. With JS off, or
   before this runs, it renders at the foot of the page — degraded, not broken.
   =========================================================================== */
(function () {
  if (!document.body || document.body.className.indexOf('temp_product') === -1) return;

  var placed = new WeakSet();

  // "See More" — the list is clamped to N items and the rest revealed on click.
  // 🔴 `hidden` on the surplus <li>, not a `max-height` clamp: the items are of
  // very different lengths, so any height that fits six on one product cuts the
  // seventh in half on another. Counting items is exact at every width.
  function clamp(panel) {
    if (!panel || panel.hasAttribute('data-pp-clamped')) return;
    var n = parseInt(panel.getAttribute('data-pp-ptab-clamp') || '0', 10);
    var list = panel.querySelector('ul');
    if (!n || !list) return;
    var items = list.children;
    if (items.length <= n) return;                 // nothing to hide, no control

    panel.setAttribute('data-pp-clamped', '');
    for (var i = n; i < items.length; i++) items[i].hidden = true;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pp_ptab_more';
    btn.textContent = panel.getAttribute('data-pp-more') || 'See More';
    btn.setAttribute('aria-expanded', 'false');
    list.insertAdjacentElement('afterend', btn);

    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') === 'true';
      for (var j = n; j < items.length; j++) items[j].hidden = open;
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      btn.textContent = open
        ? (panel.getAttribute('data-pp-more') || 'See More')
        : (panel.getAttribute('data-pp-less') || 'See Less');
    });
  }

  function place() {
    var sec = document.querySelector('.pp_ptabs_sec');
    var rte = document.querySelector('.product-details rte-formatter');
    // 🔴 A product with NO description renders no rte-formatter at all, so the
    // strip had no anchor and stayed at the foot of the page (Quiet Comforts,
    // 2026-10-26). Fall back to the slot directly BEFORE the buy buttons, which
    // is the same position in the column's order.
    var buy = rte ? null : document.querySelector('.product-details .buy-buttons-block');

    if (sec && (rte || buy) && !placed.has(sec)) {
      placed.add(sec);
      if (rte) rte.insertAdjacentElement('afterend', sec);
      else buy.insertAdjacentElement('beforebegin', sec);
      sec.classList.add('pp_ptabs_placed');
      document.documentElement.classList.add('pp_ptabs_on');
    }

    // 🔴 Every panel that ASKS to be clamped, not a hardcoded list of one.
    // `clamp()` was already generic — it reads the count off the panel — so
    // adding Brands needed no change to it, and a future tab needs none either.
    // ⚠️ `clamp()` guards itself with `data-pp-clamped`, so re-running on a
    // variant re-render cannot double-bind or re-hide an opened list.
    var panels = document.querySelectorAll('[data-pp-ptab-clamp]');
    for (var pi = 0; pi < panels.length; pi++) clamp(panels[pi]);

    // Released whatever happened above, so neither a missing section nor a
    // failed move can leave the strip permanently invisible.
    if (sec) sec.classList.add('pp_ptabs_ready');
  }

  place();
  document.addEventListener('shopify:section:load', place);
  // The details column is re-rendered on a variant change; re-place if evicted.
  setTimeout(place, 1200);
})();

/* ==========================================================================
   ADD TO CART — hide the label while the checkmark burst plays
   --------------------------------------------------------------------------
   🔴 THE MAIN BUTTON'S LABEL IS A BARE TEXT NODE, which is why Horizon's own
   `[data-added='true'] .add-to-cart-text` rule never reached it. Measured on a
   live product:

     sticky bar button   TEXT "" · SPAN.add-to-cart-text · SPAN.add-to-cart__added
     product button      TEXT "" · SPAN.add-to-cart-icon · TEXT "Add to cart" ·
                         SPAN.add-to-cart__added        <- nothing to select

   So the burst animated on top of the words. CSS cannot target a text node;
   wrapping it is the only way, and it is one span with no behaviour attached.

   ⚠️ Re-run on section load and once late: the details column is re-rendered on
   a variant change, and Horizon replaces the button with a fresh one. `wrap()`
   is idempotent — it returns immediately if the span is already there.
   ========================================================================== */
(function () {
  function wrap(btn) {
    if (!btn || btn.querySelector('.pp_atc_label')) return;
    var nodes = [].slice.call(btn.childNodes);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType === 3 && n.textContent.trim()) {
        var s = document.createElement('span');
        s.className = 'pp_atc_label';
        s.textContent = n.textContent;
        n.parentNode.replaceChild(s, n);
      }
    }
  }

  function run() {
    var btns = document.querySelectorAll('.add-to-cart-button');
    for (var i = 0; i < btns.length; i++) wrap(btns[i]);
  }

  run();
  document.addEventListener('shopify:section:load', run);
  setTimeout(run, 1200);
})();

/* ==========================================================================
   STICKY ADD TO CART — send them back to the product, not to a prompt
   --------------------------------------------------------------------------
   🔴 THE PROMPT POINTS AT A FIELD NOBODY CAN SEE. Giftship makes Message
   Greeting required, and the sticky bar only exists once the form has scrolled
   far above the fold — so clicking it fired native validation anchored to an
   off-screen input. Measured on a live product:

     sticky button   type="button", NOT inside the form, no `form` attribute
     the field       properties[Message Greeting] · required · willValidate true
     the form        checkValidity() === false

   So the click is intercepted BEFORE Horizon forwards it, and the page goes
   back to the product instead. The customer sees what is being asked of them.

   🔴 ON `window`, IN THE CAPTURE PHASE. Horizon binds its own delegated capture
   listener on `document` when its module runs in the head, which is before this
   deferred file — and capture order is registration order PER NODE, so a second
   document listener would run after theirs. One node further out always wins.
   Same reasoning as the product gallery lightbox.

   ⚠️ A VALID form is never touched: the early return runs before anything else,
   so the normal path keeps Horizon's own behaviour exactly.
   ========================================================================== */
(function () {
  function mainForm() {
    var btns = document.querySelectorAll('.add-to-cart-button');
    for (var i = 0; i < btns.length; i++) {
      if (!btns[i].closest('.sticky-add-to-cart__bar')) {
        var f = btns[i].closest('form');
        if (f) return f;
      }
    }
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  window.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('.sticky-add-to-cart__bar .add-to-cart-button');
    if (!btn) return;

    var form = mainForm();
    if (!form || form.checkValidity()) return;      // nothing wrong — let it run

    e.preventDefault();
    e.stopImmediatePropagation();

    var anchor = document.querySelector('.product-information')
              || document.querySelector('.product-details');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // 🔴 The focus is deliberately AFTER the scroll and carries
    // `preventScroll`, so the browser cannot yank the page somewhere else
    // mid-animation. `reportValidity` then puts the native message on a field
    // that is now on screen, which is the whole point of the detour.
    var bad = form.querySelector(':invalid');
    setTimeout(function () {
      if (!bad) return;
      try { bad.focus({ preventScroll: true }); } catch (err) { bad.focus(); }
      if (bad.reportValidity) bad.reportValidity();
    }, 620);
  }, true);
})();

/* ==========================================================================
   CART PAGE — re-home Giftship's cart controls
   --------------------------------------------------------------------------
   🔴 Giftship injects its whole cart integration into Horizon's PAYMENT-TERMS
   span, not into the cart. Measured on this theme 2026-09-08:

     SPAN.cart-totals__item.cart-totals__installments   <- 400px, right column
       ├── #gs__toggle-box            "Ship to Multiple Addresses"
       └── FORM#cart_form.shopify-cart-form
             ├── <shopify-payment-terms>
             └── #gsAppContainer      the REQUIRED delivery date

   It is not a bug in their script. `cart-summary.liquid` renders
   `{% form 'cart', cart %}{{ form | payment_terms }}{% endform %}` purely to
   print the Shop Pay terms, and that emits a real
   <form action="/cart" id="cart_form" class="shopify-cart-form"> — which is
   Shopify's own convention for "the cart form", and exactly what Giftship
   looks for. Horizon's ACTUAL cart form is a different element, #cart-form.

   Two consequences, both fixed here:
     1. the toggle and the date picker render in a 400px totals column
        instead of across the cart, where the live theme has them;
     2. `required` on #gsDeliveryDate never fires, because the browser only
        validates the form being submitted and the Check out button submits
        #cart-form. Re-associating the input with `form="cart-form"` restores
        the behaviour Giftship intends on a conventional theme.

   ⚠️ Only the two VISIBLE nodes are moved. `form#cart_form` and
   <shopify-payment-terms> are left exactly where Horizon put them — moving
   them would take Shopify's own Shop Pay disclosure out of the totals block.
   ========================================================================== */
(function () {
  if (!document.body.classList.contains('temp_cart')) return;

  var HOST_ID = 'pp-gs-cart';

  function host() {
    var el = document.getElementById(HOST_ID);
    if (el) return el;

    var page = document.querySelector('.cart-page');
    var items = document.querySelector('.cart-page__items');
    if (!page || !items) return null;

    el = document.createElement('div');
    el.id = HOST_ID;
    el.className = 'pp_gs_cart';
    // Above the line items, full width — where the live theme has them.
    page.insertBefore(el, items);
    return el;
  }

  // The Shipping Date belongs directly above the Checkout button, as it does on
  // the live theme — it is the last thing a shopper has to supply before paying,
  // and it is the field that blocks the button. The multi-address toggle stays
  // at the TOP of the cart, where it changes how the whole order is shipped.
  function dateHost() {
    var el = document.getElementById('pp-gs-date');
    if (el && el.isConnected) return el;

    var ctas = document.querySelector('.cart-summary__inner .cart__ctas');
    if (!ctas || !ctas.parentElement) return null;

    el = document.createElement('div');
    el.id = 'pp-gs-date';
    el.className = 'pp_gs_date';
    ctas.parentElement.insertBefore(el, ctas);
    return el;
  }

  /* 🔴 HELD BY REFERENCE (fixed 2026-10-01). Removing a line (✕) makes Horizon
     re-render the cart from the server's HTML, and that HTML has no Giftship
     nodes in it — so the morph DELETED the date field and the multi-address
     toggle we had moved into the summary, and Giftship never injects them
     again. The shopper was left with no Delivery Date and no way to set one,
     and the checkout guard skips a field that does not exist. Since 09-16.
     The detached nodes are still intact (listeners, flatpickr, a picked date),
     so they are simply put back. Same pattern as the mobile drawer's rows. */
  var held = { toggle: null, app: null };
  function grab(sel, k) {
    var el = document.querySelector(sel);
    if (el) held[k] = el;
    else if (held[k] && !held[k].isConnected) el = held[k];
    return el;
  }

  function move() {
    var h = host();
    if (!h) return;

    var moved = false;

    var toggle = grab('#gs__toggle-box', 'toggle');
    if (toggle && toggle.parentElement !== h) {
      h.appendChild(toggle);
      moved = true;
    }

    var app = grab('#gsAppContainer', 'app');
    var dh = dateHost();
    if (app && dh && app.parentElement !== dh) {
      dh.appendChild(app);
    }

    // 🔴 Re-arm the required date against the form the Check out button submits.
    // Idempotent by CONTENT, not by a flag: Horizon morphs this section on every
    // quantity change and Giftship re-renders its own fields, so a marker on the
    // node cannot be trusted to survive — the same lesson the Judge.me count and
    // date repairs taught.
    var date = document.getElementById('gsDeliveryDate');
    if (date && document.getElementById('cart-form') && date.getAttribute('form') !== 'cart-form') {
      date.setAttribute('form', 'cart-form');
    }

    info();

    if (moved) h.classList.add('is_filled');
  }

  /* ------------------------------------------------------------------------
     The Shipping Date's info control.
     🔴 The markup is a <template> rendered by `layout/theme.liquid`, never a
     string here — the same rule the product page's AI-summary heading follows,
     so the copy stays a theme setting and nothing about it needs a deploy.
     🔴 Idempotent by CONTENT, not by a flag: Giftship re-renders its own field
     on every cart morph and takes our nodes with it, so the test is "is the
     button there", never "have I run before".
     ⚠️ The BUTTON goes in the label and the PANEL beside it. A <label> forwards
     a click to the control it names, so a panel inside it would open flatpickr
     behind itself on every click.
     ------------------------------------------------------------------------ */
  function info() {
    var tpl = document.getElementById('pp-cart-date-info');
    if (!tpl || !tpl.content) return;

    var label = document.querySelector('#pp-gs-date label[for="gsDeliveryDate"]');
    if (!label || !label.parentElement) return;
    if (label.querySelector('.pp_date_info')) return;

    var frag = tpl.content.cloneNode(true);
    var btn = frag.querySelector('.pp_date_info');
    var panel = frag.querySelector('.pp_date_info_panel');
    if (!btn || !panel) return;

    label.appendChild(btn);
    label.parentElement.appendChild(panel);
  }

  move();

  // Giftship injects late, and Horizon re-renders the cart section on every
  // quantity change — so this has to keep watching rather than run once.
  var mo = new MutationObserver(move);
  mo.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', move);
  window.addEventListener('load', move);
})();

/* ==========================================================================
   CART PAGE — the Shipping Date's info panel
   --------------------------------------------------------------------------
   🔴 EVERY LISTENER IS DELEGATED AT `document` AND NOTHING IS CACHED. Horizon
   morphs the cart section on every quantity change and Giftship re-renders its
   own field inside that, so the button and the panel this drives are replaced
   repeatedly — exactly the shape that made the collection filter drawer lose
   its handlers. The nodes are looked up at event time, every time.

   🔴 `preventDefault()` ON THE BUTTON IS LOAD-BEARING. The button sits inside
   Giftship's `<label for="gsDeliveryDate">`, and a label's activation behaviour
   is the click's DEFAULT ACTION — so `stopPropagation()` at document level is
   too late to stop it (the event has already bubbled through the label by
   then). Without this, opening the panel also opens flatpickr's calendar.

   📌 The panel is positioned from two custom properties written here rather
   than from numbers in the SCSS: the label's height is whatever Giftship's type
   resolves to, and the button's x depends on how long the label's text is. Same
   mechanism as `--pp-panel-left` on the header's dropdowns.
   ========================================================================== */
(function () {
  if (!document.body.classList.contains('temp_cart')) return;

  function closest(node, sel) {
    return node && node.closest ? node.closest(sel) : null;
  }

  function close() {
    var panel = document.getElementById('pp-date-info-panel');
    if (!panel || panel.hasAttribute('hidden')) return;
    panel.setAttribute('hidden', '');
    var btn = document.querySelector('.pp_date_info');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  function open(btn, panel) {
    var field = panel.parentElement;
    if (field) {
      var f = field.getBoundingClientRect();
      var b = btn.getBoundingClientRect();
      var label = btn.parentElement;
      var l = label ? label.getBoundingClientRect() : b;

      // Where the caret points, and where the panel's BOTTOM edge sits — both
      // measured, so neither is a magic number that a type change can break.
      //
      // 🔴 IT OPENS UPWARD, and that is not the conventional direction. Opening
      // downward put a 112px panel over the date input AND the top of "Proceed
      // to Checkout" — measured, and visible in the first screenshot. Covering
      // the one control this page exists for is worse than briefly covering the
      // tax note and the total above it, which the shopper gets back the moment
      // the panel closes.
      panel.style.setProperty('--pp-info-x', Math.round(b.left + b.width / 2 - f.left) + 'px');
      panel.style.setProperty('--pp-info-bottom', Math.round(f.bottom - l.top + 11) + 'px');
    }
    panel.removeAttribute('hidden');
    btn.setAttribute('aria-expanded', 'true');
  }

  document.addEventListener('click', function (e) {
    var btn = closest(e.target, '.pp_date_info');
    var panel = document.getElementById('pp-date-info-panel');
    if (!panel) return;

    if (btn) {
      e.preventDefault();               // 🔴 or the label opens the calendar too
      if (panel.hasAttribute('hidden')) open(btn, panel); else close();
      return;
    }

    if (closest(e.target, '.pp_date_info_panel')) {
      // Inside the panel: cancel the label's forwarding, but never a link's own
      // navigation — the whole point of the note is the link in it.
      if (!closest(e.target, 'a')) e.preventDefault();
      return;
    }

    close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var panel = document.getElementById('pp-date-info-panel');
    if (!panel || panel.hasAttribute('hidden')) return;
    close();
    var btn = document.querySelector('.pp_date_info');
    if (btn) btn.focus();
  });
})();

/* ==========================================================================
   CART DRAWER — close on a click outside the panel
   --------------------------------------------------------------------------
   🔴 Horizon opens this drawer with dialog.show(), NOT showModal() — measured,
   `dialog.matches(':modal')` is false. A non-modal dialog gets no backdrop and
   no light-dismiss from the browser, so an outside click did nothing (Esc did
   work — that is Horizon's own handler). The scrim is painted as `body::before`
   in `_cartDrawer.scss`, so a click on it lands on <body>.

   ⚠️ Closed by clicking HORIZON'S OWN toggle rather than calling close() — the
   component tracks its own open state and animation, and closing the element
   behind its back leaves that state stale.
   ========================================================================== */
(function () {
  document.addEventListener('click', function (e) {
    if (e.target !== document.body) return;

    // 🔴 ANY open `<theme-drawer>`, not just the cart. "View store information"
    // opens a second one, and it shares this panel's geometry and scrim — so it
    // has to share the light-dismiss too, or one drawer closes on an outside
    // click and the other does not.
    var drawers = document.querySelectorAll('theme-drawer');
    for (var i = 0; i < drawers.length; i++) {
      var dlg = drawers[i].querySelector('dialog');
      if (!dlg || !dlg.open) continue;

      // ⚠️ Closed through the drawer's OWN close button rather than
      // `dialog.close()`. The component tracks its open state and runs its
      // closing animation from there; closing the element behind its back
      // leaves that state stale. The cart's toggle would also work, but the
      // pickup drawer's trigger is a link further down the page — its own
      // close button is the one control every theme-drawer has.
      var close = drawers[i].querySelector('.theme-drawer__close-button');
      if (close) close.click();
      return;
    }
  });
})();

/* ==========================================================================
   KLAVIYO FLOATING TEASER — move it off the sticky add-to-cart bar
   --------------------------------------------------------------------------
   Reported 2026-09-08: the floating gift icon sits bottom-CENTRE and lands on
   top of the product page's sticky Add-to-cart bar.

   🔴 It is KLAVIYO'S, not Giftship's — the element carries
   `kl-private-reset-css-*`, which is Klaviyo's CSS-isolation class. So the
   tidiest fix is in Klaviyo's own form editor (teaser position), and this is
   the belt-and-braces version for whatever it renders.

   🔴 EVERY CLASS ON IT IS HASHED PER RELEASE (`Teaser-pointer-kSCH8`,
   `kl-private-reset-css-Xuajs1`), so nothing here matches a full class name —
   it matches the stable PREFIXES, then walks up to whichever ancestor is
   actually `position: fixed`. That is the element that has to move, and which
   one it is differs between Klaviyo's form types.

   Placement, per Simranjeet:
     desktop  bottom-LEFT   (the sticky bar is centred, so left is clear)
     mobile   bottom-RIGHT, lifted ABOVE the sticky bar rather than beside it —
              at phone widths the bar is full width, so there is no clear corner
              at the same height.

   ⚠️ NOT VERIFIED IN THE ISOLATED BROWSER — Klaviyo's display rules keep the
   teaser from rendering there at all (0 matches after a 15s wait, a scroll and
   a storage clear), so this was written against the element Simranjeet
   screenshotted. Check it on a real session.
   ========================================================================== */
(function () {
  var GAP = 16;

  function teaser() {
    // The span he inspected, matched on prefixes only.
    var el = document.querySelector('[class*="Teaser"][class*="kl-private-reset-css"]')
      || document.querySelector('[class*="kl-private-reset-css"][class*="Teaser"]')
      || document.querySelector('[class*="Teaser-pointer"]');
    return el || null;
  }

  function fixedAncestor(el) {
    var node = el;
    while (node && node !== document.body) {
      if (getComputedStyle(node).position === 'fixed') return node;
      node = node.parentElement;
    }
    return null;
  }

  // The height of whatever bottom-docked bar is on screen, so the teaser can be
  // lifted clear of it rather than guessing a constant.
  function barHeight() {
    var h = 0;
    ['.sticky-add-to-cart__bar', '#PBarNextFrameWrapper'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (bar) {
        var cs = getComputedStyle(bar);
        if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return;
        var r = bar.getBoundingClientRect();
        // Only count it if it is actually docked to the bottom of the viewport.
        if (r.height > 10 && r.bottom > window.innerHeight - 4) h = Math.max(h, r.height);
      });
    });
    return h;
  }

  function place() {
    var el = teaser();
    if (!el) return;

    var box = fixedAncestor(el);
    if (!box) return;

    var mobile = window.matchMedia('(max-width: 989px)').matches;
    var bottom = (mobile ? barHeight() + GAP : GAP) + 'px';
    var side = mobile ? 'right' : 'left';
    var other = mobile ? 'left' : 'right';

    // Idempotent by CONTENT, not by a flag — Klaviyo re-renders and re-positions
    // its own container, so a marker on the node cannot be trusted to survive.
    if (box.style.bottom === bottom && box.style[side] === GAP + 'px' && box.style[other] === 'auto') return;

    var st = box.style;
    st.setProperty('bottom', bottom, 'important');
    st.setProperty('top', 'auto', 'important');
    st.setProperty(side, GAP + 'px', 'important');
    st.setProperty(other, 'auto', 'important');
    // Klaviyo centres it with a translate; that would undo the corner placement.
    st.setProperty('transform', 'none', 'important');
    st.setProperty('margin', '0', 'important');
  }

  place();
  window.addEventListener('load', place);
  window.addEventListener('resize', place);
  window.addEventListener('scroll', place, { passive: true });

  // The teaser is injected late and re-rendered on its own schedule, and the
  // sticky bar appears and disappears with scroll — so this keeps watching.
  new MutationObserver(place).observe(document.body, { childList: true, subtree: true });
})();

/* ==========================================================================
   CART PAGE — the Shipping Date really does block checkout
   --------------------------------------------------------------------------
   🔴 GIFTSHIP'S `required` CAN NEVER FIRE, HERE OR ON THE LIVE THEME. Measured
   on `#gsDeliveryDate`:

     required: true   readOnly: true   willValidate: FALSE   checkValidity(): true

   Flatpickr makes its input `readonly` so the calendar is the only way to set a
   date — and the HTML spec bars a readonly control from constraint validation.
   So the attribute is decorative: `form.checkValidity()` returns true with the
   field empty, whichever form the input belongs to. (Re-associating it with
   Horizon's `#cart-form` was still worth doing — it is the right form — but it
   was never going to be enough on its own.)

   On the live theme the enforcement comes from Giftship's OWN submit handler on
   its own `<form class="shopify-cart-form">`. Horizon's Check out button is in a
   different form and never reaches that handler, which is why the field looked
   optional here.

   So the check is ours. It runs on `window` in the CAPTURE phase so it lands
   before Horizon's own click handling, and it only ever blocks — it never
   submits anything itself.

   ⚠️ Deliberately narrow: it fires ONLY when the field is present and visible.
   If Giftship is disabled, uninstalled, or the date is not configured for a
   given cart, nothing here can stop a shopper checking out.
   ========================================================================== */
(function () {
  if (!document.body.classList.contains('temp_cart')) return;

  var MSG = 'Please choose a shipping date before checking out.';

  function field() {
    var el = document.getElementById('gsDeliveryDate');
    if (!el) return null;
    // Not rendered, or hidden by the app for this cart — nothing to enforce.
    if (!el.offsetParent && getComputedStyle(el).position !== 'fixed') return null;
    return el;
  }

  function errorNode(host) {
    var el = host.querySelector('.pp_gs_date_err');
    if (el) return el;
    el = document.createElement('p');
    el.className = 'pp_gs_date_err';
    el.setAttribute('role', 'alert');
    el.textContent = MSG;
    host.appendChild(el);
    return el;
  }

  function clear() {
    var host = document.getElementById('pp-gs-date');
    if (!host) return;
    host.classList.remove('has_error');
    var e = host.querySelector('.pp_gs_date_err');
    if (e) e.classList.remove('is_shown');
  }

  window.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest
        ? e.target.closest('#checkout, [name="checkout"], .cart__checkout-button')
        : null;
      if (!btn) return;

      var date = field();
      if (!date || date.value.trim() !== '') return;

      e.preventDefault();
      e.stopImmediatePropagation();

      var host = document.getElementById('pp-gs-date');
      if (host) {
        host.classList.add('has_error');
        errorNode(host).classList.add('is_shown');
      }

      // The input is readonly, so focusing it opens flatpickr rather than a
      // keyboard — which is exactly the next thing the shopper needs.
      try {
        date.scrollIntoView({ block: 'center', behavior: 'smooth' });
        date.focus();
      } catch (err) {}
    },
    true
  );

  // Clear the state as soon as a date is picked. `change` is what flatpickr
  // fires; `input` never does on a readonly field.
  document.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'gsDeliveryDate' && e.target.value.trim() !== '') clear();
  });
})();

/* ==========================================================================
   GIFTSHIP — a real quantity stepper, matching the cart page
   --------------------------------------------------------------------------
   Giftship renders a bare `<input type="number">` in two places: each row of the
   multi-address page, and its "Edit" product modal. Styling the BOX to match the
   cart page got it most of the way, but the browser's native spinner arrows are
   what was left — visually heavy, and nothing like the cart's − / + pair.

   🔴 THE INPUT IS THEIRS AND STAYS THEIRS. The buttons are added AROUND it and
   they only write `input.value`, then dispatch `input` and `change` so
   Giftship's own listeners run exactly as if the shopper had typed. Nothing
   here talks to the cart.

   ⚠️ Idempotent by CONTENT, not by a flag: both hosts re-render (the modal is
   rebuilt on every Edit click, and the page re-renders on a quantity change), so
   a marker on the node cannot be trusted to survive. The test is whether the
   input's parent is already one of our boxes.
   ========================================================================== */
(function () {
  if (!document.body || document.body.className.indexOf('pg_gs_proxy') === -1) return;

  function step(input, delta) {
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;

    var now = parseInt(input.value, 10);
    if (isNaN(now)) now = min;

    var next = now + delta;
    if (next < min) next = min;
    if (next === now) return;

    input.value = next;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* 🔴 The MODIFIER is what lets the SCSS draw the two glyphs as identical
     bars. They were typeface characters — "−" (U+2212) and "+" — and no
     typeface draws those two at the same width or the same stroke: measured in
     ivyepic at 15px, the minus inked 8.8px and the plus 9.9px, which is what
     was reported as "the + and − are not matching". The text is still written
     so the control reads correctly with no CSS. */
  function button(label, aria, mod) {
    var b = document.createElement('button');
    b.type = 'button';                 // never submit the form it sits in
    b.className = 'pp_qty_btn ' + mod;
    b.setAttribute('aria-label', aria);
    b.textContent = label;
    return b;
  }

  function wrap(input) {
    var parent = input.parentElement;
    if (!parent || parent.classList.contains('pp_qty')) return;

    var box = document.createElement('div');
    box.className = 'pp_qty';

    var minus = button('−', 'Decrease quantity', 'pp_qty_minus');
    var plus = button('+', 'Increase quantity', 'pp_qty_plus');

    parent.insertBefore(box, input);
    box.appendChild(minus);
    box.appendChild(input);
    box.appendChild(plus);

    minus.addEventListener('click', function () { step(input, -1); });
    plus.addEventListener('click', function () { step(input, 1); });
  }

  function build() {
    document
      .querySelectorAll('input.gs__cart-qty, #gsEditProductModalContainer input[name="quantity"]')
      .forEach(wrap);
  }

  build();
  window.addEventListener('load', build);
  new MutationObserver(build).observe(document.body, { childList: true, subtree: true });
})();

/* ==========================================================================
   CART DRAWER — close it when a link inside is followed
   --------------------------------------------------------------------------
   🔴 THE DRAWER SURVIVES THE NAVIGATION. Horizon uses view transitions for page
   navigation and deliberately excludes open drawers from the cross-fade —
   `theme-drawer-styles.liquid` has a rule to keep them "frozen (visually
   unchanged) while the rest of the page transitions". So the <dialog> is still
   open on the page you land on, which reads as the drawer having failed to
   close (reported 2026-09-08 after the Selling Fast disc became a link).

   ⚠️ A modified click is left alone. cmd/ctrl/shift/middle opens a new tab and
   the current page does not navigate at all, so closing the drawer there would
   shut it under the shopper for no reason.

   📌 Closed through the drawer's OWN close button, not `dialog.close()` — the
   component tracks its open state and runs its closing animation from there.
   ========================================================================== */
(function () {
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest ? e.target.closest('a[href]') : null;
    if (!link) return;

    var wrap = document.getElementById('cart-drawer');
    if (!wrap || !wrap.contains(link)) return;

    var dlg = wrap.querySelector('dialog');
    if (!dlg || !dlg.open) return;

    // Only a real navigation. An in-page anchor or a JS-driven control should
    // leave the drawer alone.
    var href = link.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || link.target === '_blank') return;

    var close = wrap.querySelector('.theme-drawer__close-button');
    if (close) close.click();
  });
})();

/* ==========================================================================
   SEARCH MODAL — the empty state rebuilds itself once, and it must not do it
   UNDERNEATH the shopper
   --------------------------------------------------------------------------
   🔴 THE CAUSE IS HORIZON'S, AND IT IS A RACE YOU CANNOT WIN — ONLY ABSORB.
   Reported 2026-09-09 and pinned down by Simranjeet himself: it happens *only*
   when the search field is clicked **while the page is still loading**, and only
   on the first open.

   `assets/predictive-search.js` → `#resetSearch()`:

     1. fetch section `predictive-search-empty`       <- the "Products" list
     2. fetch section `predictive-search` (id: query) <- "Recently Viewed"
     3. collectionElement.prepend(...recentlyViewed)  <- ABOVE the products
     4. morph() the result in

   Two sequential round trips that cannot even START until the module lands, and
   the second block is PREPENDED — so everything already on screen is pushed
   down when it arrives. Measured on this store: module ready ~1.1s, both
   sections complete ~2.0-2.5s, `load` ~2.2-2.7s. A shopper who clicks search a
   second into the page load is inside that window BY DEFINITION, and no amount
   of starting earlier closes it: the data depends on two network requests that
   cannot be made any sooner.

   ⚠️ NOTHING IN CSS CAN CAUSE THIS. It is a DOM insertion from a deferred
   module, so it is unrelated to the price/title spacing pass it was first
   noticed alongside.

   📌 SO THE FIX IS VISUAL, NOT TEMPORAL, AND IT IS IN THREE PARTS.
   ========================================================================== */
(function () {
  var KEY = 'viewedProducts';   // = RecentlyViewed.#STORAGE_KEY
  var HOLD_MS = 700;            // see part 2

  function expectsSwap() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) && v.length > 0;
    } catch (e) {
      // A private window, blocked storage, or corrupt JSON. Horizon's own read
      // would throw here too, so the honest answer is "no swap is coming".
      return false;
    }
  }

  var host = null;
  function modal() {
    if (!host) host = document.getElementById('search-modal');
    return host;
  }
  function built() {
    var m = modal();
    return !!(m && m.querySelector('.recently-viewed-wrapper'));
  }

  /* ---- 1. Start it as early as the module allows --------------------------
     🔴 Dispatch `dialog:open` rather than calling the public `resetSearch()`,
     and that detail is why there is no duplicate work: their
     `#handleDialogOpen` calls the PRIVATE `#loadEmptyState()`, which sets
     `#emptyStateLoaded = true` before starting — so their own
     `requestIdleCallback` (which has NO timeout, and on a 345-resource page
     gets no slot until after `load`) early-returns instead of re-running both
     fetches and morphing identical content in. These responses carry no
     `cache-control`, so a duplicate pass is a full ~125ms round trip each.

     ✅ Safe to synthesise: `dialog:open` is emitted only by `dialog.js`, and
     `predictive-search.js` holds the ONLY listener for it in the theme.
     ⚠️ That listener is `{ once: true }`, so this consumes it — correct,
     because `#loadEmptyState` is idempotent by the same flag.
     ⚠️ Gated on `viewedProducts`, mirroring their `connectedCallback` exactly:
     a visitor who has them already pays for these two requests on every page
     view, so this adds nothing and only stops them arriving late; a visitor
     with none triggers no swap, so we fire nothing.
     📌 This alone does NOT fix the reported case — it only shrinks the window.
     Parts 2 and 3 are what make the window harmless.                        */
  if (expectsSwap() && window.customElements && customElements.whenDefined) {
    customElements.whenDefined('predictive-search-component').then(function () {
      var m = modal();
      if (!m || !m.querySelector('predictive-search-component') || built()) return;
      try {
        m.dispatchEvent(new CustomEvent('dialog:open'));
      } catch (e) {
        /* their internals are theirs — a failure here must never break the page */
      }
    });
  }

  /* ---- 2 + 3. Absorb the window that is left ----------------------------- */
  document.addEventListener('dialog:open', function (e) {
    var m = modal();
    if (!m || e.target !== m) return;
    if (!expectsSwap() || built()) return;   // nothing is coming, or it is already here

    /* 2. HOLD the body briefly rather than show a list that is about to be
          rebuilt. The panel is playing its own 300ms slide-in at this moment,
          so a short quiet body reads as the modal settling. Capped, and
          released the instant the swap lands — usually well inside the cap. */
    m.classList.add('pp_ps_pending');

    var done = false;
    function release(late) {
      if (done) return;
      done = true;
      obs.disconnect();
      clearTimeout(timer);
      m.classList.remove('pp_ps_pending');

      /* 3. If we ran out of patience and revealed the products first, the swap
            is still in flight — so let it land BELOW instead of above. CSS
            reorders it, so when it finally arrives nothing already on screen
            moves. This is the part that makes the worst case harmless rather
            than merely less likely.
            ⚠️ It stays for the life of the page view: re-ordering it back once
            settled would be the very jump this exists to prevent. */
      if (late) m.classList.add('pp_ps_late');
    }

    var obs = new MutationObserver(function () { if (built()) release(false); });
    obs.observe(m, { childList: true, subtree: true });
    var timer = setTimeout(function () { release(true); }, HOLD_MS);
  }, true);   // capture — `dialog:open` is dispatched on the element, not bubbled from within
})();

/* ==========================================================================
   HASH LANDING — land ON the section, do not animate down to it
   --------------------------------------------------------------------------
   🔴 The suppression itself is NOT here. `.pp_hash_jump` is set by an inline
   script in <head> (`layout/theme.liquid`), because the browser performs its
   own scroll to `#anchor` as soon as the target has been parsed and laid out —
   which is BEFORE a deferred script runs. Anything at this point could only
   interrupt the animation, never prevent it. This block only cleans up after it.

   Two jobs:
   1. RE-SNAP while the layout can still move. The target sits below sections
      full of images; each one that loads shifts it, so the position the browser
      picked at parse time is not where the section ends up. Re-snapping on
      `load` and on `document.fonts.ready` costs nothing and is invisible —
      `scroll-behavior` is still `auto` at that point.
   2. RELEASE the class, so ordinary in-page smooth scrolling (the FAQ tabs, the
      collection toolbar, home section 2's rail) works normally afterwards.

   ⚠️ Any deliberate input cancels the re-snap. Yanking a visitor back to an
   anchor they have already scrolled away from is worse than landing slightly
   off, and it is the failure mode people notice.
   📌 `scrollIntoView()` honours `scroll-margin-top`, which is where the
   header-height offset lives (`--pp-chrome-h` + 24) — so the offset is declared
   once, in CSS, and nothing here needs to know how tall the header is.
   ========================================================================== */
(function () {
  var root = document.documentElement;
  if (!root.classList.contains('pp_hash_jump')) return;

  function release() { root.classList.remove('pp_hash_jump'); }

  var id = '';
  try {
    id = decodeURIComponent(location.hash.slice(1));
  } catch (e) {
    id = location.hash.slice(1);
  }
  if (!id) { release(); return; }

  var target = null;
  try {
    target = document.getElementById(id);
  } catch (e) {
    target = null;
  }
  if (!target) { release(); return; }

  var cancelled = false;
  function cancel() { cancelled = true; }
  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (ev) {
    window.addEventListener(ev, cancel, { passive: true, once: true });
  });

  function snap() {
    if (cancelled) return;
    try {
      target.scrollIntoView();
    } catch (e) {
      /* nothing here is worth breaking the page for */
    }
  }

  snap();

  window.addEventListener('load', function () {
    snap();
    // One frame after the last image has landed, then hand smooth scrolling back.
    setTimeout(function () { snap(); release(); }, 250);
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(snap).catch(function () {});
  }

  // Belt and braces: `load` can be a long way off on a heavy page, and the class
  // must never be left on — it would silently disable smooth scrolling for the
  // rest of the visit.
  setTimeout(release, 4000);
})();

/* ------------------------------------------------------------------ *
 * FOOTER NEWSLETTER — clear ?customer_posted=true after it has shown
 * ------------------------------------------------------------------ *
 * Shopify redirects a successful `{% form 'customer' %}` to
 * `<current page>?customer_posted=true#contact_form`, and Liquid's
 * `form.posted_successfully?` is driven by that PARAMETER, not by anything
 * about this visit. So the confirmation is re-rendered on every reload of
 * that URL, and on any back-navigation to it — reported 2026-09-14 as
 * "even after page reload the message appears".
 *
 * 🔴 The message itself is NOT touched. It is already painted by the time
 * this runs, and removing it would throw away the one piece of feedback the
 * visitor gets. Only the URL is rewritten, so THIS view keeps the
 * confirmation and the next load is clean.
 *
 * 📌 `replaceState`, never `pushState` — a history entry the visitor can go
 * "back" into would restore the parameter and the message with it.
 * 📌 The hash is dropped with it. The browser has already scrolled to the
 * footer form by the time this runs (it scrolls on load, from the hash as it
 * was), so the visitor still lands on the confirmation; clearing it just
 * stops a reload jumping there again.
 */
(function () {
  if (!window.history || !history.replaceState) return;
  if (location.search.indexOf('customer_posted') === -1) return;
  if (!document.querySelector('.pp_footer_form_msg')) return;

  try {
    var url = new URL(location.href);
    url.searchParams.delete('customer_posted');
    url.hash = '';
    history.replaceState({}, '', url.pathname + url.search);
  } catch (e) {
    /* URL unsupported or a malformed href — leaving the parameter in place is
       the harmless outcome, so never let this throw into the rest of the file. */
  }
})();

/* ---------------------------------------------------------------------------
 * SEARCH — exclude Giftship's add-on products AT THE QUERY, not in the template
 * ---------------------------------------------------------------------------
 * Every search form on the store carries `data-pp-search-exclude`, rendered by
 * `snippets/pp-search-exclude.liquid`. On submit the fragment is added to the
 * `q` this navigation sends, so Shopify itself never returns an add-on.
 *
 * 🔴 IT HAS TO BE THE QUERY. Filtering `search.results` inside `{% paginate %}`
 * thins a page Shopify has already sized and counted, so the header reported
 * the unfiltered total and Load More fetched pages that then rendered one or
 * two cards — the bug this exists to fix. `pp-is-addon` stays in `coll-grid`
 * as the backstop for a no-JS submit or an inbound `/search?q=` link.
 *
 * 🔴 IT BUILDS THE URL AND NAVIGATES; IT DOES NOT TOUCH THE INPUT. The first
 * version appended the fragment to `field.value` and let the form submit
 * normally — which flashed `-tag:OPTIONS_HIDDEN_PRODUCT` into the search box
 * the visitor was looking at, and left it sitting there behind the modal.
 * Reported immediately. Every other field is carried across unchanged, so the
 * hidden `type` and `options[prefix]` inputs still apply.
 * ⚠️ The fragment is still visible in the ADDRESS BAR — Shopify offers no
 * separate parameter for it, `q` is the only way in, and a second `q` is
 * ignored (measured). The displayed count, the repopulated field and the
 * "no results for …" line all strip it back out (`pp-search-terms`).
 *
 * 📌 DELEGATED ON `document`, in the BUBBLE phase, so it covers the search
 * page's form, the 404's, and Horizon's own modal form — including its "View
 * all", which is a submit button inside that same form. Nothing is bound per
 * form, so a re-rendered section cannot lose it.
 *
 * ⚠️ NEVER APPENDED TO AN EMPTY QUERY. `q=-tag:OPTIONS_HIDDEN_PRODUCT` on its
 * own is not "no results", it is EVERY product that is not an add-on — 352 of
 * them — so a visitor pressing Enter on an empty field would get the whole
 * catalogue instead of being left where they were.
 */
(function () {
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM' || e.defaultPrevented) return;

    var token = (form.getAttribute('data-pp-search-exclude') || '').trim();
    if (!token) return;

    var field = form.querySelector('input[name="q"]');
    if (!field) return;

    var q = (field.value || '').trim();
    if (!q) return;                                   /* see the note above */
    if (q.indexOf(token) !== -1) return;              /* already carried */

    var url;
    try {
      url = new URL(form.getAttribute('action') || Theme.routes.search_url, location.origin);
    } catch (err) {
      return; /* malformed action — let the browser submit it as it would have */
    }

    /* Carry every other field the form declares, then override `q` alone. */
    var data;
    try {
      data = new FormData(form);
    } catch (err) {
      return;
    }
    data.forEach(function (value, key) {
      if (key === 'q') return;
      url.searchParams.append(key, value);
    });
    url.searchParams.set('q', q + ' ' + token);
    // Out-of-stock (Coming Soon) products rank by relevance, as in the modal.
    url.searchParams.set('options[unavailable_products]', 'show');

    e.preventDefault();
    window.location.href = url.toString();
  });
})();

/* ==========================================================================
   COMING SOON — the notice in the product details column (2026-09-25)
   --------------------------------------------------------------------------
   The buy controls are already hidden by CSS off the `pp_coming_soon` body
   class, so this only ADDS the notice. It goes directly before
   `.buy-buttons-block`, i.e. where Add to Cart was, and after the product tabs
   (which insert themselves after the description).
   🔴 Idempotent by CONTENT: it looks for the notice in the column rather than
   trusting a flag, because Horizon re-renders the details column on a variant
   change and a flag would survive on a node the notice no longer sits in.
   ========================================================================== */
(function () {
  if (!document.body || !document.body.classList.contains('pp_coming_soon')) return;
  var tpl = document.getElementById('pp-coming-soon');
  if (!tpl) return;

  function place() {
    var buy = document.querySelector('.product-details .buy-buttons-block');
    var col = buy ? buy.parentElement : document.querySelector('.product-details .group-block-content');
    if (!col || col.querySelector('.pp_soon_note')) return;
    var note = tpl.content.firstElementChild.cloneNode(true);
    if (buy) buy.insertAdjacentElement('beforebegin', note);
    else col.appendChild(note);
  }

  place();
  document.addEventListener('shopify:section:load', place);
  setTimeout(place, 1200);
})();

/* ==========================================================================
   PRE-ORDER — product page (2026-10-01, under test on two UNLISTED products)
   --------------------------------------------------------------------------
   Driven by the `pp_preorder` body class and `#pp-preorder` (layout/theme.liquid),
   i.e. by the product's `pre-order` tag + `custom.preorder_ship_date`. Three jobs:

   1. The NOTICE ("Pre-order · Ships from …") directly before the buy buttons.
   2. (REMOVED 2026-10-02) There is no hidden line marker any more. A `_`
      property is hidden from the SHOPPER but the admin order shows it, so
      staff saw "_Pre-order ships from" under the picked date. The picked date
      itself (Giftship field `Pre-order ship date`, required) is the marker:
      the cart and the multi-address script both key off it.
   3. The DATE LOCK. Any date picker in the product form — Giftship's
      product-option date field — gets the ship-from date as its earliest
      selectable day, and a date already picked before it is cleared.
      ⚠️ Giftship has no "not before a fixed date" setting (only rolling lead
      times), which is why this is ours.

   🔴 Idempotent by CONTENT, never by a flag: Horizon re-renders the details
   column on a variant change and Giftship renders its fields late, so every
   pass checks what is actually there and writes only what is missing.
   ========================================================================== */
(function () {
  if (!document.body || !document.body.classList.contains('pp_preorder')) return;
  var tpl = document.getElementById('pp-preorder');
  if (!tpl) return;

  var ISO = tpl.getAttribute('data-ship-from') || '';
  var HUMAN = tpl.getAttribute('data-ship-label') || '';

  // Local midnight of the ship-from day. `new Date('2026-11-16')` would be UTC
  // midnight, i.e. the 15th in Toronto, which unlocks one day too early.
  var MIN = null;
  if (ISO) {
    var p = ISO.split('-');
    MIN = new Date(+p[0], +p[1] - 1, +p[2]);
  }

  function forms() {
    return document.querySelectorAll(
      '.product-details form[action*="/cart/add"], product-form-component form'
    );
  }

  function note() {
    var buy = document.querySelector('.product-details .buy-buttons-block');
    var col = buy ? buy.parentElement : document.querySelector('.product-details .group-block-content');
    if (!col || col.querySelector('.pp_pre_note')) return;
    var el = tpl.content.firstElementChild.cloneNode(true);
    if (buy) buy.insertAdjacentElement('beforebegin', el);
    else col.appendChild(el);
  }

  /* Ontario statutory holidays, COMPUTED for any year — so nobody has to type
     next year's dates into Giftship. 🔴 Giftship's own "Disable specific dates"
     list is SAVED but NOT APPLIED on its product-option date picker (measured
     2026-10-01: Dec 25 stayed selectable with it in the list), so this is the
     only thing blocking holidays on the pre-order date at all.
     Same rules as the store's hand-typed cart list, plus Boxing Day. A holiday
     on a weekend is NOT moved to the Monday (the cart list does not do that
     either; the client's call). Easter = Anonymous Gregorian computus, the
     algorithm `snippets/pp-occasion-date.liquid` already uses. */
  function nthMonday(y, m, n) {
    var d = new Date(y, m, 1);
    var first = 1 + ((8 - d.getDay()) % 7);
    return new Date(y, m, first + (n - 1) * 7);
  }
  function easter(y) {
    var a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    var f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31) - 1, day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(y, month, day);
  }
  var HOLS = {};
  function key(d) { return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate(); }
  function holidaysFor(y) {
    if (HOLS[y]) return HOLS[y];
    var gf = easter(y); gf.setDate(gf.getDate() - 2);
    var vic = new Date(y, 4, 24); vic.setDate(24 - ((vic.getDay() + 6) % 7));   // Monday on or before May 24
    var list = [
      new Date(y, 0, 1),            // New Year's Day
      nthMonday(y, 1, 3),           // Family Day
      gf,                           // Good Friday
      vic,                          // Victoria Day
      new Date(y, 6, 1),            // Canada Day
      nthMonday(y, 7, 1),           // Civic Holiday
      nthMonday(y, 8, 1),           // Labour Day
      nthMonday(y, 9, 2),           // Thanksgiving (Canadian)
      new Date(y, 11, 25),          // Christmas Day
      new Date(y, 11, 26)           // Boxing Day
    ];
    var set = {};
    list.forEach(function (d) { set[key(d)] = true; });
    return (HOLS[y] = set);
  }
  function isHoliday(date) { return !!holidaysFor(date.getFullYear())[key(date)]; }
  isHoliday.__ppHol = true;

  function lock() {
    if (!MIN) return;
    forms().forEach(function (f) {
      f.querySelectorAll('input').forEach(function (i) {
        var fp = i._flatpickr;
        if (fp) {
          var dis = (fp.config && fp.config.disable) || [];
          if (!dis.some(function (x) { return x && x.__ppHol; })) {
            fp.set('disable', dis.concat([isHoliday]));
          }
          var cur = fp.config && fp.config.minDate;
          if (!cur || cur.getTime() < MIN.getTime()) fp.set('minDate', MIN);
          var picked = fp.selectedDates && fp.selectedDates[0];
          if (picked && picked.getTime() < MIN.getTime()) fp.clear();
          return;
        }
        if (i.type === 'date' && i.min !== ISO) {
          i.min = ISO;
          if (i.value && i.value < ISO) i.value = '';
        }
      });
    });
  }

  var queued = false;
  function run() {
    queued = false;
    note();
    lock();
  }
  function soon() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(run);
  }

  run();
  // Giftship injects its fields after load and Horizon re-renders on a variant
  // change. Every write above is conditional on something being MISSING, so a
  // pass triggered by our own write finds nothing to do and the loop settles.
  new MutationObserver(soon).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', soon);
  window.addEventListener('load', soon);
})();

/* ==========================================================================
   PRE-ORDER — cart page notice (2026-10-01)
   --------------------------------------------------------------------------
   Reads `data-pp-line` on each cart row (set in `snippets/cart-products.liquid`:
   `pre` / `addon` / `normal`) and shows ONE notice above the Shipping Date:

     pre + normal lines -> "mixed": pre-orders ship on their own dates, the
                           date below is for the other items;
     pre lines only     -> "only":  there is no date to choose, and the Shipping
                           Date field is hidden (`.pp_pre_only` on <body>).

   🔴 Giftship add-on lines are ignored: a greeting card bought WITH a pre-order
   ships with it, so it must not turn the cart "mixed".
   🔴 Decided from the ROWS, not in Liquid at page load: Horizon re-renders the
   cart on every quantity change and removal, with no page load.
   ⚠️ Hiding the field also disarms the "choose a date" checkout guard above —
   that guard skips a field that is not rendered (`offsetParent` null).
   ========================================================================== */
(function () {
  if (!document.body || !document.body.classList.contains('temp_cart')) return;
  var tpl = document.getElementById('pp-preorder-cart');
  if (!tpl) return;

  function mode() {
    var rows = document.querySelectorAll('.cart-page [data-pp-line], .cart-items [data-pp-line]');
    var pre = 0, normal = 0;
    rows.forEach(function (r) {
      var k = r.getAttribute('data-pp-line');
      if (k === 'pre') pre++;
      else if (k === 'normal') normal++;
    });
    if (!pre) return '';
    return normal ? 'mixed' : 'only';
  }

  // 🔴 The notice lives in a FIXED holder, `#pp-pre-cart`, rendered by
  // `cart-summary.liquid` with `data-skip-subtree-update`. Never insert a loose
  // node into the summary: the morph reuses it as the next server node and the
  // old text survives inside that node (it showed the notice twice).
  function sync() {
    var m = mode();
    var host = document.getElementById('pp-pre-cart');
    var only = m === 'only';

    if (document.body.classList.contains('pp_pre_only') !== only) {
      document.body.classList.toggle('pp_pre_only', only);
    }
    if (!host) return;

    var cur = host.firstElementChild;
    var curMode = cur ? cur.getAttribute('data-pp-pre-mode') || '' : '';
    if (curMode === m && host.children.length <= 1) return;

    while (host.firstChild) host.removeChild(host.firstChild);
    if (!m) return;
    var src = tpl.content.querySelector('[data-pp-pre-mode="' + m + '"]');
    if (src) host.appendChild(src.cloneNode(true));
  }

  var queued = false;
  function soon() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; sync(); });
  }

  sync();
  new MutationObserver(soon).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('load', soon);
})();

/* ==========================================================================
   GIFTSHIP REQUIRED FIELD: inline message instead of a native alert
   (2026-10-26, audit)
   --------------------------------------------------------------------------
   Giftship's "Message Greeting" is required. Pressing Add to Cart with it empty
   fired a NATIVE `alert("Please complete the required fields")` that names no
   field, and on a phone the field is often off screen or inside a collapsed
   `.pp_gs_row`. Giftship also marks the field `gs__input-error`, which is the
   only hook it gives us, so:
     1. that ONE alert text is intercepted (every other alert goes through);
     2. the first field carrying `gs__input-error` is revealed (its accordion
        row opened), scrolled to the middle of the screen and focused;
     3. a note sits under it until the visitor types.
   🔴 FAILS OPEN: if no marked field is found the original alert still shows,
   so a Giftship markup change can never swallow the message silently.
   ========================================================================== */
(function () {
  if (!document.body) return;
  var cls = document.body.className;
  if (cls.indexOf('temp_product') === -1 && cls.indexOf('pg_gs_proxy') === -1) return;

  var nativeAlert = window.alert;
  var MSG = 'Please fill in this field to continue.';

  function firstErrorField() {
    var root = document.querySelector('#gsAppContainer, .gs__product-app-container, #gsEditProductModalContainer');
    if (!root) return null;
    var list = root.querySelectorAll('.gs__product-element.gs__input-error, .gs__input-error textarea, .gs__input-error input:not([type=hidden]), .gs__input-error select');
    for (var i = 0; i < list.length; i++) {
      if (list[i].type !== 'hidden') return list[i];
    }
    return null;
  }

  function showInline(field) {
    var row = field.closest('details');
    if (row && !row.open) row.open = true;

    var holder = field.closest('.gs__product-option') || field.parentNode;
    if (holder && !holder.querySelector('.pp_gs_req_msg')) {
      var note = document.createElement('p');
      note.className = 'pp_gs_req_msg';
      note.setAttribute('role', 'alert');
      note.textContent = MSG;
      field.insertAdjacentElement('afterend', note);
      field.setAttribute('aria-invalid', 'true');
      var clear = function () {
        if (!field.value || !field.value.trim()) return;
        note.remove();
        field.removeAttribute('aria-invalid');
        field.removeEventListener('input', clear);
        field.removeEventListener('change', clear);
      };
      field.addEventListener('input', clear);
      field.addEventListener('change', clear);
    }

    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); }
  }

  window.alert = function (message) {
    if (typeof message === 'string' && /complete the required fields/i.test(message)) {
      var field = firstErrorField();
      if (field) { showInline(field); return; }
    }
    return nativeAlert.apply(window, arguments);
  };
})();
